// ============================================================
// GAME — core state machine
// ============================================================

import {
  BATTLE_COLS, BATTLE_ROWS, ELEM, ECOL, SPELLS, BASES,
  SCHED_TIMER_SECS,
  CARD_OPENING_DRAW, CARD_DRAW_PER_TURN,
} from './constants.ts';
import { md, clamp, resetId } from './utils.ts';
import { anim } from './Anim.ts';
import { BattleUnit, Base, Pos, mkBase, mkUnit, unitFromJson, baseFromJson } from './BattleUnit.ts';
import type { ServerAdapter } from './client/ServerAdapter.ts';
import { LocalServerAdapter } from './client/LocalServerAdapter.ts';
import { NetworkServerAdapter } from './client/NetworkServerAdapter.ts';
import { resolveNetUrl, resolveNetMode, saveNetUrl, clearNetUrl } from './client/netUrl.ts';
import type { BattleEvent, SpellCommand, SpellCastRecord } from './server/types.ts';
import { _pushModifier, _recomputeScalars } from './server/spell.ts';
import { SeededRng, randomSeed } from './server/rng.ts';
import {
  type TerrainCell,
  type TerrainGrid,
  generateTerrain,
  flipTerrainForView,
  growTransition,
  rainTransition,
} from './server/terrain.ts';
import { Deployment, generateAiUnits } from './Deployment.ts';
import {
  type DeploymentRules,
  DEFAULT_MANUAL_RULES,
  PRESET_RULES,
} from './balance.ts';
import { getLevel, levelPlayerDeployed, type LevelDef } from './LevelData.ts';
import { UCFG } from './constants.ts';
import * as ArmyData from './ArmyData.ts';
import { Deck } from './cards/Deck.ts';
import { getDeckTemplate } from './cards/DeckTemplates.ts';
import { CARDS } from './cards/CardData.ts';
import { planAiSchedule, buildCast } from './cards/CardScheduler.ts';
import type { CardOp } from './cards/CardTypes.ts';
import { W, OYB, CB, ZBY, cellToScreen, ISO_DEPTH } from './canvas.ts';
import { showPrompt } from './prompt.ts';
// 循環相依（Renderer 亦 import Game）：僅於方法內於 runtime 取用 static
// 座標矩形，讓飛行卡動畫與 Input 命中共用同一份 rect（ESM 延遲解析、安全）。
import { Renderer } from './Renderer.ts';

// Card Tactician (M6b) — three消 / Story / Casual modes removed. The card
// pipeline is now the only path: MENU → DEPLOY → SCHED (draw + play cards)
// → EXEC → OVER, with PvP DeckMirror anti-cheat (see netserver/RoomRegistry).
export const GS = { MENU: 0, DEPLOY: 1, SCHED: 3, EXEC: 4, OVER: 5, LOBBY: 6, LEVELS: 7 } as const;
export type GameState = typeof GS[keyof typeof GS];

export interface FX {
  type: string;
  x?: number; y?: number;
  x1?: number; y1?: number; x2?: number; y2?: number;
  text?: string;
  color: string;
  alpha: number;
  timer: number;
  size?: number;
  radius?: number;
  /** For particle effects */
  dx?: number; dy?: number;
  /** For death skull animation */
  scale?: number;
}

export interface AvailSpell {
  el: number;
  lv: number;
}

/**
 * Card Tactician — 飛行卡片動畫（純視覺層，**不動 deck 邏輯狀態**）。
 *   - 'draw'：卡背從牌庫堆飛入手牌槽 + 翻面(flip 0→1) + 淡入 + 放大。
 *   - 'play'：手牌飛進 tick 槽 + 縮小 + 淡出（放置回饋）。
 * 由 `anim` tween 驅動 x/y/scale/alpha/flip；`delay` 做逐張 stagger；tween
 * 完成的 onDone 把自己從 `Game.cardFx` 移除。座標一律取自 `Renderer` static
 * rect（deckPileRect / cardHandRect / cardTickRect），與 Input 命中同源。
 */
export interface CardFx {
  kind: 'draw' | 'play';
  cardId: string;
  /** 對應手牌 uid（'draw' 用來在飛行期間隱藏該手牌靜態卡避免重影）。 */
  uid?: string;
  x: number; y: number;
  scale: number;
  alpha: number;
  flip: number;
  /** stagger 倒數；>0 時停在起點、尚未 kick off tween。 */
  delay: number;
  started: boolean;
  dur: number;
  /** tween 終點。 */
  tx: number; ty: number; tscale: number; talpha: number;
}

export class Game {
  state: GameState = GS.MENU;
  round = 0;
  maxRounds = 10;
  units: BattleUnit[] = [];
  bases: Base[] = [];
  deployment: Deployment = new Deployment();
  pSpells: (SpellCommand | null)[] = [null, null, null];
  aSpells: (SpellCommand | null)[] = [null, null, null];
  spellSlot = -1;

  /**
   * Card Tactician (M3/M6) — runtime decks. Non-null in card mode.
   * Created in `startBattle`; hand persists across rounds (攢牌 = 技術).
   * `_enterRoundCards` draws opening 5 / +3 per turn.
   *   - single : `playerDeck` = team myTeam, `aiDeck` = opponent (planned
   *              locally via planAiSchedule).
   *   - PvP (M6): only `playerDeck` is built client-side; the opponent's
   *              deck is authoritative on the server and hidden, so `aiDeck`
   *              stays undefined and no AI planning runs.
   */
  playerDeck?: Deck;
  aiDeck?: Deck;
  /** M6 — this client's deck seed (sent to server in PvP so its authoritative
   *  Deck draws/reshuffles identically). Stashed in `startBattle`. */
  playerDeckSeed = 0;
  /** M4c — 佈陣選擇的牌組原型 id（DECK_TEMPLATES）。startBattle 用來建 Deck。 */
  playerDeckId = 'starter';
  aiDeckId = 'storm';
  /** 單機關卡制：目前選中的關卡（決定 AI 牌組 + 雙方固定單位/位置）。PvP 為 null。 */
  activeLevel: LevelDef | null = null;
  /** M4b — 每個 tick 已出掉的牌 uid（清除 tick 時用來還牌）。每回合重置。 */
  tickCards: string[][] = [[], [], []];
  /** M4b — 目前 tick 正在組的疊加佇列（放置前，尚未離開手牌）。 */
  cardQueueEl = 0;
  cardQueueUids: string[] = [];
  /** M4c — 調度（cycle）選棄模式：點調度進入、點手牌標記要棄的牌、再點調度確認。 */
  cycleMode = false;
  cycleCardUid?: string;
  cycleMarks: string[] = [];
  cycleLimit = 2;
  /** M4c — 搜牌（tutor）選牌模式：點搜牌進入、從牌庫選一張上手。 */
  tutorMode = false;
  tutorCardUid?: string;
  schedStep: 'slot' | 'target' | 'confirm' = 'slot';

  /**
   * Session 6c follow-up — bottom interaction zone is now a 2-tab view.
   *  - 'match3' tab: 6×6 gem board (interactive in MATCH3, dimmed in SCHED/EXEC)
   *  - 'sched'  tab: scheduling slots + spell picker (interactive in SCHED;
   *                  read-only summary in MATCH3 / EXEC)
   * Auto-switches at phase boundaries; player can manually toggle to peek.
   */
  bottomTab: 'match3' | 'sched' = 'match3';

  /**
   * Session 6c follow-up — when MATCH3 ends (timer expired OR swap cap),
   * defer the SCHED transition until the M3Board's animation queue drains
   * (`!board.locked`). Avoids cutting off mid-cascade visual feedback.
   */
  pendingSched = false;

  /**
   * Session 6c follow-up — once `pendingSched` && board has settled, hold
   * for ~1.4s with a "進入魔法部屬" transition banner before actually
   * flipping to SCHED. Smooths the MATCH3→SCHED handoff (felt jarring
   * before — board went still, tab flicked, no breathing room).
   */
  schedEnterDelay = 0;
  schedEnterTriggered = false;

  /**
   * Session 6c follow-up — sticky banner that shows the current EXEC
   * sub-step (Friendly Spells / Move / Combat / Hostile Spells / Contest).
   * Replaces the prior fade-out floatText approach so the label stays put
   * until the next sub-step replaces it.
   */
  phaseBanner: { text: string; color: string } | null = null;
  selSpellType = 0;
  availSpells: AvailSpell[] = [];
  usedSpellEls: number[] = [];
  timer = 0;
  targeting = false;
  targetPos: Pos | null = null;
  // Execution
  adapter!: ServerAdapter;
  events: BattleEvent[] = [];
  stepIdx = 0;
  stepTimer = 0;
  stepActive = false;
  stepApplied = false;
  /** Phase 3c Step 5: set while executeExec RPC is in flight. Blocks _updateExec. */
  execPending = false;
  /** Phase 3c Step 5: set while initBattle RPC is in flight. Blocks state progression. */
  initPending = false;
  /**
   * Session 6c follow-up — human-readable label shown by the loading
   * overlay (`Renderer._loadingOverlay`) while initPending is true.
   * Defaults to a generic "Connecting" if left unset.
   */
  loadingLabel: string | null = null;
  /** Phase 3c Step 5: latest adapter error surfaced to the HUD (optional UI). */
  adapterError: string | null = null;
  // Visual FX
  fx: FX[] = [];
  /** Card Tactician — 飛行卡片動畫（抽/出牌，純視覺；見 CardFx）。 */
  cardFx: CardFx[] = [];
  /** 每 tick 放置閃光值（0..1；confirmTarget 觸發、_updateCardFx 衰減）。 */
  tickFlash: number[] = [0, 0, 0];
  /** v2：該 tick 已用來「跳過並從棄牌抽 1 張」（暗掉、不可放 cast、不可反悔）。每回合重置。 */
  tickDrawUsed: boolean[] = [false, false, false];
  winner = -1;
  selectedCell: { r: number; c: number } | null = null;
  pressedCell: { r: number; c: number } | null = null;
  /** Timer for selected-cell pulse animation */
  selPulse = 0;
  /** Resource flash timers for HUD pop animation */
  resFlash: Record<string, number> = {};
  /** Phase transition overlay */
  transitionTimer = 0;
  transitionLabel = '';
  private _aiTimer = 0;
  /** Cached snapshot of save slots — refreshed when entering deploy / after mutations */
  private _slotsCache: (ArmyData.ArmyData | null)[] = [null, null, null];
  /** Pending slot action toggle in deployment UI: 'none' | 'save' | 'clear' */
  slotAction: 'none' | 'save' | 'clear' = 'none';
  /** Phase 3c: whether to route battle through NetworkServerAdapter. */
  netMode = false;
  /**
   * Phase 3c / 5a: server URL when in netMode.
   * Resolved by `resolveNetUrl()` (see `client/netUrl.ts`):
   *   URL param > localStorage > build env > 'ws://localhost:8080'.
   * Mutable: `promptServerUrl()` updates this and persists to localStorage.
   */
  netUrl = 'ws://localhost:8080';
  /**
   * Phase 3c Step 6: master RNG seed for the current battle.
   * Regenerated on each startBattle(); also echoed to the server via
   * initBattle so replays/audits have the exact seed that produced the
   * session. 0 = "no battle yet".
   */
  masterSeed = 0;
  /**
   * Phase 3e: this caller's per-team M3Board seed.
   * Derived in `startBattle()` from the master seed. Shipped to server
   * via `initBattle` so the server can build a `Match3Mirror` for
   * anti-cheat replay validation. 0 = "no battle yet".
   */
  playerBoardSeed = 0;

  /**
   * Terrain (special battlefield cells — grass / puddle / burningGrass).
   * Stored in VIEW coords (already flipped for team-1 guest); Renderer
   * reads it directly without any further coord juggling.
   *
   * Lifecycle:
   *   - Single-player: picked at `startSinglePlayer` so terrain is
   *     visible during DEPLOY for path planning.
   *   - PvP: server sends `terrainSeed` in the createRoom / joinRoom
   *     RPC results; `startCreateRoom` / `startJoinRoom` apply it.
   *     `battleReady` later ships the authoritative grid which we
   *     overwrite the local copy with (defensive — should be byte-
   *     identical to what we generated locally).
   *   - Reset to `null` by `restart()`.
   */
  terrain: TerrainGrid | null = null;

  /**
   * The seed `terrain` was generated from. Echoed to the server in
   * `initBattle` so single-mode authoritative state agrees on the grid.
   * 0 = "no terrain yet".
   */
  terrainSeed = 0;

  // ----------------------------------------------------------
  // Phase 3d Step 3 — PvP mode plumbing
  // ----------------------------------------------------------

  /**
   * Which mode the current session is running in.
   *   - 'single'    : local single-player vs the in-process AI (DEFAULT)
   *   - 'pvp-host'  : created a room, waiting for / paired with a guest
   *   - 'pvp-guest' : joined someone else's room
   *
   * Drives whether _aiMatch3 fires, whether _enterSched auto-fills the
   * opponent's schedule, and which team field every outgoing RPC tags
   * itself with.
   */
  gameMode: 'single' | 'pvp-host' | 'pvp-guest' = 'single';

  // ----------------------------------------------------------
  // Surrender (MATCH3-only escape hatch — long-press 5s → confirm)
  // ----------------------------------------------------------
  /** Seconds the surrender button has been held this gesture. */
  surrenderHoldTime = 0;
  /** True while player is actively pressing the surrender button. */
  surrenderHolding = false;
  /** True after 5s hold completed — confirm modal is showing. */
  surrenderConfirmActive = false;
  /** Visual hold target in seconds — exposed for renderer progress bar. */
  static readonly SURRENDER_HOLD_SECS = 5;

  /** Which team the local player is. Single player is always 0. */
  myTeam: 0 | 1 = 0;

  /** PvP room code. null in single-player. */
  roomCode: string | null = null;

  /** Short status text shown over the lobby state. */
  lobbyMessage: string = '';

  /** True while we're awaiting an async room RPC (createRoom / joinRoom). */
  lobbyPending: boolean = false;

  /**
   * Deployment phase deadline (ms since epoch) sent by the server when
   * a multiplayer room is fully joined. Single-player or pre-join is null.
   */
  deployDeadline: number | null = null;

  /** PvP guest's session id once paired (for diagnostics / Step 5 disconnect handling). */
  opponentSessionId: string | null = null;

  // Phase 3d Step 4 — PvP synchronisation flags
  /**
   * True while we're sitting on a phase boundary waiting for the other
   * player. Renderer overlays "Waiting for opponent" when this is set.
   * Cleared whenever the awaited server push arrives.
   */
  waitingForOpponent: boolean = false;

  /**
   * Set by handleInitBattle response in PvP mode. While true,
   * startBattle has fired initBattle but battleReady push hasn't
   * arrived yet — the round can't start.
   */
  awaitingBattleReady: boolean = false;

  /**
   * Phase 3d Step 5 — set when the server pushes opponentDisconnected.
   * Drives the "Opp disconnected" subtitle on the OVER screen and
   * prevents the handler from running twice.
   */
  opponentLeft: boolean = false;

  /**
   * Set when our OWN WebSocket closed unexpectedly (network drop, server
   * kill, mobile-tab background, etc.) while a PvP session was live.
   * Drives the "(連線中斷)" subtitle on the OVER screen and prevents
   * `_handleConnectionLost` from running twice.
   *
   * Mirror of `opponentLeft` for the disconnected-side: the *other* side
   * of the same network event gets `opponentDisconnected` from the
   * server; this side learns about it locally via the synthetic
   * `connectionLost` event emitted by NetworkServerAdapter.
   */
  selfDisconnected: boolean = false;

  // Phase 3d Step 4 fix: view orientation
  /**
   * True when the local player's view should be flipped 180° relative
   * to server world coords. Currently only true for the PvP guest
   * (team 1) so they see their own units at the bottom in blue and
   * opponent at the top in red — same UX as the host.
   *
   * IMPORTANT: this is the SINGLE source of truth. Inbound server data
   * (units, bases, event positions) is flipped to view coords on
   * receipt; outbound client data (spell targetPos, deployment unit
   * positions in initBattle) is flipped back to world coords just
   * before sending. Renderer & most game logic operate on view coords
   * exclusively — no per-call-site coordinate gymnastics.
   */
  get viewFlipped(): boolean { return this.myTeam === 1; }

  /** View ↔ world along X (self-inverse). */
  viewFlipX(x: number): number {
    return this.viewFlipped ? (BATTLE_COLS - 1 - x) : x;
  }
  /** View ↔ world along Y (self-inverse). */
  viewFlipY(y: number): number {
    return this.viewFlipped ? (BATTLE_ROWS - 1 - y) : y;
  }

  /** Convenience: flip a Pos. */
  private _flipPos(p: Pos): Pos {
    return { x: this.viewFlipX(p.x), y: this.viewFlipY(p.y) };
  }

  /**
   * Apply view flip to all coordinate fields inside every event type.
   * Returns the same array if no flip needed (host / single).
   * Mutates clones — caller-supplied events untouched.
   */
  private _flipEventsForView(events: BattleEvent[]): BattleEvent[] {
    if (!this.viewFlipped) return events;
    const fp = (p: Pos) => this._flipPos(p);
    return events.map((ev: any) => {
      // Deep-clone first so we don't scribble on shared/server objects.
      const e = JSON.parse(JSON.stringify(ev));
      switch (e.type) {
        case 'spell':
          for (const s of e.spells || []) {
            if (s.tgt) s.tgt = fp(s.tgt);
            if (s.cmd?.targetPos) s.cmd.targetPos = fp(s.cmd.targetPos);
            // Session 9 — whirlwind embeds MoveRecord[] with from/to
            // positions; flip them just like top-level MoveEvent does.
            if (Array.isArray(s.moves)) {
              for (const m of s.moves) {
                if (m.from) m.from = fp(m.from);
                if (m.to) m.to = fp(m.to);
              }
            }
          }
          break;
        case 'move':
          for (const m of e.moves || []) { m.from = fp(m.from); m.to = fp(m.to); }
          for (const c of e.contested || []) {
            c.pos = fp(c.pos);
            c.unitAFrom = fp(c.unitAFrom);
            c.unitBFrom = fp(c.unitBFrom);
          }
          break;
        case 'combat':
          for (const g of e.groups || []) {
            for (const a of g.attacks || []) {
              if (a.attackerPos) a.attackerPos = fp(a.attackerPos);
              if (a.targetUnitPos) a.targetUnitPos = fp(a.targetUnitPos);
              if (a.targetBasePos) a.targetBasePos = fp(a.targetBasePos);
            }
          }
          break;
        case 'contestResolve':
          for (const r of e.retreats || []) { r.from = fp(r.from); r.to = fp(r.to); }
          for (const s of e.stays || []) { s.pos = fp(s.pos); }
          break;
        case 'terrainIgnite':
        case 'terrainGrow':
        case 'terrainRain':
        case 'terrainBoulderFall':
        case 'terrainBoulderBreak':
          // a-2 + Session 11 + Session 12 — server emits world-coord
          // cells; guest view flips them so the local terrain grid
          // (already in view coords) mutates at the right indices in
          // `_applyEvent`. Same shape for all five terrain-mutation
          // events (Ignite / Grow / Rain / BoulderFall / BoulderBreak),
          // so a fall-through is safe. terrainGrow / terrainRain carry
          // a `to` field per cell that's invariant under flip; the new
          // boulder events are just `Pos[]`.
          if (Array.isArray(e.cells)) {
            e.cells = e.cells.map((p: Pos) => fp(p));
          }
          break;
        case 'terrainDot':
          // applies are unit-keyed (no positional fields), nothing to flip.
          break;
      }
      return e;
    });
  }

  /**
   * Pre-warmed adapter for the lobby flow. In single-player we still build
   * the adapter inside startBattle (legacy path). In PvP we need the adapter
   * BEFORE startBattle so we can call createRoom / joinRoom from the menu.
   */
  private _lobbyUnsubs: Array<() => void> = [];

  constructor() {
    // Phase 5a Step 1: net-mode + URL resolution moved to client/netUrl.ts.
    // Priority: ?net=ws://... URL param > localStorage > VITE_DEFAULT_WS_URL > fallback.
    this.netMode = resolveNetMode();
    this.netUrl = resolveNetUrl();
    if (this.netMode) {
      console.log(`[game] net mode ON — will use NetworkServerAdapter (${this.netUrl})`);
    }
    // Start at main menu
    this.state = GS.MENU;
  }

  /** Transition from Menu to Deployment.
   *
   *  Pre-condition: caller (`startSinglePlayer` / `startCreateRoom` /
   *  `startJoinRoom` / `startStoryChapter` / `startCasualLevel`) has
   *  already called `this.deployment.setRules(...)` with the appropriate
   *  rule set. `setRules` itself does the full reset (clears `placed`,
   *  palette counters, drag state, onChange hook), so this method just
   *  handles mode-specific post-setup (story hooks, casual preset load,
   *  autosave wiring for manual modes). */
  startDeployment(): void {
    this.state = GS.DEPLOY;
    this.slotAction = 'none';
    // Wire autosave: fire on every deployment mutation.
    this.deployment.onChange = () => {
      ArmyData.saveAutosave(ArmyData.serialize(this.deployment.placed));
    };
    // Restore last session's deployment if an autosave exists.
    const auto = ArmyData.loadAutosave();
    if (auto) this.deployment.loadFromUnits(ArmyData.toDeployedUnits(auto));
    this._refreshSlotsCache();
  }

  // ----------------------------------------------------------
  // SAVE SLOTS — manual Save/Load during deployment
  // ----------------------------------------------------------

  /** Save the current deployment to manual slot N. Returns success flag. */
  saveToSlot(slot: number): boolean {
    if (this.state !== GS.DEPLOY) return false;
    if (this.deployment.placed.length === 0) {
      this._slotToast('Place at least 1 unit first', '#ff8844');
      return false;
    }
    const ok = ArmyData.saveSlot(slot, ArmyData.serialize(this.deployment.placed));
    if (ok) this._slotToast(`Saved to Slot ${slot + 1}`, '#44ff88');
    else this._slotToast('Save failed', '#ff6644');
    this._refreshSlotsCache();
    return ok;
  }

  /** Load manual slot N into the current deployment. Returns success flag. */
  loadFromSlot(slot: number): boolean {
    if (this.state !== GS.DEPLOY) return false;
    const data = ArmyData.loadSlot(slot);
    if (!data) { this._slotToast(`Slot ${slot + 1} is empty`, '#ff8844'); return false; }
    this.deployment.loadFromUnits(ArmyData.toDeployedUnits(data));
    this._slotToast(`Loaded Slot ${slot + 1}`, '#44ccff');
    return true;
  }

  /** Clear manual slot N. */
  clearSlot(slot: number): void {
    if (this.state !== GS.DEPLOY) return;
    ArmyData.clearSlot(slot);
    this._slotToast(`Cleared Slot ${slot + 1}`, '#ff8844');
    this._refreshSlotsCache();
  }

  /**
   * Dispatch a tap on slot N according to the current slotAction toggle.
   * - 'save'  → saveToSlot + reset toggle
   * - 'clear' → clearSlot + reset toggle
   * - 'none'  → load if slot is filled (otherwise show hint)
   */
  triggerSlot(slot: number): void {
    if (this.state !== GS.DEPLOY) return;
    if (this.slotAction === 'save') {
      this.saveToSlot(slot);
      this.slotAction = 'none';
    } else if (this.slotAction === 'clear') {
      this.clearSlot(slot);
      this.slotAction = 'none';
    } else {
      this.loadFromSlot(slot);
    }
  }

  /** Toggle the save/clear slot action indicator. */
  toggleSlotAction(action: 'save' | 'clear'): void {
    if (this.state !== GS.DEPLOY) return;
    this.slotAction = this.slotAction === action ? 'none' : action;
  }

  /** Snapshot the 3 slots (for UI) — reads from cache. Null entries = empty slots. */
  listSlots(): (ArmyData.ArmyData | null)[] {
    return this._slotsCache;
  }

  private _refreshSlotsCache(): void {
    this._slotsCache = ArmyData.listSlots();
  }

  /** Flash a short status label on screen as feedback for save/load actions. */
  private _slotToast(text: string, color: string): void {
    this.fx.push({
      type: 'floatText',
      x: W / 2,
      y: OYB + CB * 3.5,
      text,
      color,
      alpha: 2.0,
      timer: 1.4,
      size: 15,
    });
  }

  /**
   * Transition from Deployment to Battle — takes units from deployment.
   *
   * Phase 3c Step 5: now async. For the local adapter this resolves
   * synchronously. For the network adapter we await the server ack
   * before starting the first round — otherwise a swap issued in the
   * first 100ms could race ahead of initBattle on the wire.
   */
  async startBattle(): Promise<void> {
    if (!this.deployment.canConfirm()) return;
    if (this.initPending) return; // guard against double-click
    resetId();

    // Seed lifecycle: one master seed → deterministic deck seeds (deploy +
    // player deck + AI deck). Reproducing a session needs only masterSeed.
    this.masterSeed = randomSeed();
    const master = new SeededRng(this.masterSeed);
    const deploySeed = master.nextUint();
    const playerDeckSeed = master.nextUint();
    const aiDeckSeed = master.nextUint();
    this.playerDeckSeed = playerDeckSeed >>> 0;
    {
      const pTmpl = getDeckTemplate(this.playerDeckId) ?? getDeckTemplate('starter');
      this.playerDeck = pTmpl ? new Deck(pTmpl.cards, playerDeckSeed) : undefined;
      // Single-player simulates the AI's deck locally (planAiSchedule). PvP
      // (M6): the opponent's deck lives on the server (authoritative + hidden),
      // so we never build it client-side — aiDeck stays undefined.
      if (this.gameMode === 'single') {
        const aTmpl = getDeckTemplate(this.aiDeckId) ?? getDeckTemplate('storm');
        this.aiDeck = aTmpl ? new Deck(aTmpl.cards, aiDeckSeed) : undefined;
      } else {
        this.aiDeck = undefined;
      }
    }
    console.log('[game] seed master=' + this.masterSeed + ' deploy=' + deploySeed);

    // Build the unit roster.
    //   Player: `exportPlayerUnits(myTeam, viewFlipped)` — single source.
    //   AI:     single generates a mirrored roster (offline PvP variant);
    //           PvP merges the opponent's roster server-side on battleReady.
    const playerUnits = this.deployment.exportPlayerUnits(this.myTeam, this.viewFlipped);
    let aiUnits: BattleUnit[] = [];
    if (this.gameMode === 'single' && this.activeLevel) {
      // 關卡制：AI 單位由關卡固定（各帶朝下的簡單前進路徑）。
      aiUnits = this._buildLevelAiUnits(this.activeLevel);
    } else if (this.gameMode === 'single' && this.deployment.rules.kind === 'manual') {
      aiUnits = generateAiUnits(this.deployment.rules.composition, new SeededRng(deploySeed));
    }
    this.units = [...playerUnits, ...aiUnits];
    this.bases = BASES.map(b => mkBase(b.team, b.tiles, b.hp));
    this.round = 0;
    this.fx = [];
    this.winner = -1;
    this.events = [];

    // Phase 3d Step 4: PvP keeps the adapter created back in
    // startCreateRoom / startJoinRoom. Single / Story / Casual create a
    // fresh LocalServerAdapter here.
    //
    // Session 11 follow-up #2 — removed the legacy `(netMode && single)
    // → NetworkServerAdapter` dev override. Reasoning:
    //   - Test Mode (= single) is explicitly the "local-vs-AI sandbox"
    //     per the menu UI copy. Sending it through the network adapter
    //     is never what the user wants.
    //   - The override caused stuck "Initialising battle" / "Connecting
    //     to server" overlays whenever the WS server was unreachable,
    //     both on Android packaged shell (netMode auto-on from
    //     `isPackagedShell()`) AND on web dev with `?net=1` in the URL.
    //   - Devs who want to test the network code path can play PvP via
    //     Create Room / Join Room — those still use NetworkServerAdapter
    //     (and pre-create it earlier in the lobby flow so connection
    //     errors surface in a more obvious place).
    //
    // After this change, Test Mode is fully offline regardless of
    // netMode / URL params / packaged shell status. PvP and lobby flows
    // are untouched.
    if (this._isLocalMode()) {
      this.adapter = new LocalServerAdapter();
      // Card Tactician (M3) — single-player runs card-mode schedule
      // validation (hand-driven, no Points & Tiers gate).
      if (this.cardMode) (this.adapter as LocalServerAdapter).enableCardMode();
    } else {
      // Phase 3d Step 4: arm the waiting flags BEFORE awaiting initBattle.
      // The server's battleReady push can race with the initBattle rpcResult
      // (server fires push first, then ack). If we don't arm these now, a
      // fast push would be ignored and we'd sit in DEPLOY forever.
      this.awaitingBattleReady = true;
      this.waitingForOpponent = true;
    }

    this.initPending = true;
    this.adapterError = null;
    // Label reflects actual adapter: NetworkServerAdapter (PvP only —
    // single is always Local after Session 11 follow-up #2) needs a WS
    // connection → show "Connecting to server" so a hang at this stage
    // clearly points at the network. LocalServerAdapter is sync + offline
    // → "Initialising battle" is honest.
    this.loadingLabel = this.adapter instanceof NetworkServerAdapter
      ? 'Connecting to server'
      : 'Initialising battle';
    // Session 6c follow-up: enforce a minimum loading-overlay duration
    // so single-player (LocalServerAdapter resolves synchronously) still
    // sees the spinner. ~700ms feels long enough to register but short
    // enough not to annoy.
    const MIN_LOADING_MS = 700;
    const minLoadingDelay = new Promise<void>((r) => setTimeout(r, MIN_LOADING_MS));
    try {
      await Promise.all([
        this.adapter.initBattle(
          this.units,
          this.bases,
          this.masterSeed,
          this.playerBoardSeed,
          this.terrainSeed,
          // PvP：把本 client 的 deckId + deckSeed 送 server，建權威 Deck（反作弊）。
          // 單機 LocalServerAdapter 忽略。
          this.playerDeckId,
          this.playerDeckSeed,
        ),
        minLoadingDelay,
      ]);
    } catch (err) {
      this.adapterError = `initBattle failed: ${(err as Error).message}`;
      console.error('[game]', this.adapterError);
      this.initPending = false;
      this.loadingLabel = null;
      return;
    }
    this.initPending = false;
    this.loadingLabel = null;

    if (this._isLocalMode()) {
      this._startRound();
    } else {
      // If the battleReady push already arrived during await, the handler
      // has already cleared awaitingBattleReady and called _startRound —
      // don't clobber state in that case.
      if (this.awaitingBattleReady) {
        this.lobbyMessage = 'Deployment sent. Waiting for opponent...';
        this.state = GS.LOBBY;
      }
    }
  }

  /** True for any mode that uses the local in-process adapter (single).
   *  Used to gate PvP-only RPC flows and timer countdowns. */
  private _isLocalMode(): boolean {
    return this.gameMode === 'single';
  }

  /**
   * Card Tactician — the card「取魔法」pipeline is now the only path
   * (three消 removed in M6b). Always true; kept as a getter so existing
   * call sites read clearly. DEPLOY → 抽牌 → SCHED → EXEC, SCHED assigns
   * hand cards to ticks, spell.ts uses stackCount. PvP adds server-side
   * deck-ownership validation (see netserver/RoomRegistry RoomBattle).
   */
  get cardMode(): boolean {
    return true;
  }

  private _startRound(): void {
    this.round++;
    if (this.round > this.maxRounds) { this._endGame(); return; }
    // Card pipeline: draw this round's cards, then straight to SCHED.
    this._enterRoundCards();
  }

  /**
   * Card Tactician (M3) — per-round entry for card mode. Replaces the
   * MATCH3 gem-collection phase with: 補抽手牌 (起手 5 / 每回合 +3，吃 §14
   * 常數；手牌跨回合保留、超過上限自動棄) → 進入 SCHED 出牌規劃。
   *
   * AI 的抽牌同步在這裡進行；AI 的出牌規劃延到 `_finalizeSched`（盤面狀態
   * 與玩家確認時一致）。沒有計時壓力（單機 SCHED 不倒數），等玩家按確認。
   */
  private _enterRoundCards(): void {
    // Reset per-round scheduling state (mirror the三消 _startRound resets).
    this.pSpells = [null, null, null];
    this.aSpells = [null, null, null];
    this.spellSlot = 0;
    this.schedStep = 'slot';
    this.selSpellType = 0;
    this.targeting = false;
    this.targetPos = null;
    this.usedSpellEls = [];
    this.phaseBanner = null;
    this.pendingSched = false;
    this.schedEnterDelay = 0;
    this.schedEnterTriggered = false;
    this.bottomTab = 'sched';
    this.tickCards = [[], [], []];
    this.cardQueueEl = 0;
    this.cardQueueUids = [];
    this.cycleMode = false;
    this.cycleCardUid = undefined;
    this.cycleMarks = [];
    this.tutorMode = false;
    this.tutorCardUid = undefined;
    this.cardFx = [];
    this.tickFlash = [0, 0, 0];
    this.tickDrawUsed = [false, false, false];

    // 補抽：起手（round 1）抽 CARD_OPENING_DRAW，其後每回合 CARD_DRAW_PER_TURN。
    // hand 跨回合保留（Deck 內部不清），draw 自動處理上限 / 洗牌回收。
    const opening = this.round === 1;
    const n = opening ? CARD_OPENING_DRAW : CARD_DRAW_PER_TURN;
    const drawn = this.playerDeck?.draw(n) ?? [];
    this.aiDeck?.draw(n);
    // 抽牌飛行動畫（玩家；AI 抽牌隱藏、不做視覺）。
    this._spawnDrawFx(drawn.map((c) => c.uid));

    // 進入 SCHED（單機無倒數；timer 設值僅供 HUD 顯示一致）。
    this.state = GS.SCHED;
    this.timer = SCHED_TIMER_SECS;
    this.transitionTimer = opening ? 0.8 : 1.4;
    this.transitionLabel = opening ? `Round ${this.round}` : `進入出牌 (Round ${this.round})`;
    this._refreshAvailSpells();
  }

  update(dt: number): void {
    anim.update(dt);
    this._updateCardFx(dt);
    this.selPulse += dt * 4;
    // Tick transition
    if (this.transitionTimer > 0) this.transitionTimer -= dt;
    // Tick resource flash timers
    for (const k in this.resFlash) {
      this.resFlash[k] -= dt;
      if (this.resFlash[k] <= 0) delete this.resFlash[k];
    }
    // Update FX
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i];
      f.timer -= dt;
      if (f.type === 'floatText') { f.y! -= 25 * dt; f.alpha -= dt * 0.7; }
      if (f.type === 'm3Score') {
        // Points & Tiers: "+N" travels from gem centre toward the HUD
        // element bar with an exponential ease, fading as it goes.
        // x2/y2 are the target. Using an instantaneous ease (not a
        // pre-baked tween) so it stays smooth across resize.
        const k = Math.min(1, dt * 3.2);
        f.x! += (f.x2! - f.x!) * k;
        f.y! += (f.y2! - f.y!) * k;
        f.alpha -= dt * 1.1;
      }
      if (f.type === 'ring') { f.radius! += 60 * dt; f.alpha -= dt * 1.2; }
      if (f.type === 'slash') { f.alpha -= dt * 1.5; }
      if (f.type === 'moveTrail') { f.alpha -= dt * 2.0; }
      if (f.type === 'particle') { f.x! += f.dx! * dt; f.y! += f.dy! * dt; f.dy! += 80 * dt; f.alpha -= dt * 1.5; }
      if (f.type === 'comboBanner') {
        // Scale-up bounce: 0.4 → 1.2 → fade. Fully bouncy: scale grows
        // toward 1.2 with overshoot, alpha fades after ~0.4s.
        const target = 1.2;
        f.scale = (f.scale || 0.4) + (target - (f.scale || 0.4)) * Math.min(1, dt * 8);
        // Hold alpha for first 0.5s, then fade.
        if (f.timer < 0.5) f.alpha -= dt * 2.2;
      }
      if (f.type === 'flash') {
        // Full-screen colour overlay — quick decay so the screen doesn't
        // stay tinted past the moment of impact.
        f.alpha -= dt * 1.2;
        if (f.alpha < 0) f.alpha = 0;
      }
      if (f.type === 'deathX') { f.alpha -= dt * 0.8; f.scale = (f.scale || 1) + dt * 0.5; }
      if (f.type === 'moveArrow') { f.alpha -= dt * 1.8; }
      if (f.timer <= 0) this.fx.splice(i, 1);
    }

    // Surrender is a one-tap → confirm flow during the card battle
    // (SCHED / EXEC). Clear residual state once we leave those phases.
    const inCardBattle = this.state === GS.SCHED || this.state === GS.EXEC;
    if (!inCardBattle && (this.surrenderHolding || this.surrenderConfirmActive)) {
      this.surrenderHolding = false;
      this.surrenderHoldTime = 0;
      this.surrenderConfirmActive = false;
    }

    switch (this.state) {
      case GS.MENU:
        break;
      case GS.DEPLOY:
        break;
      case GS.SCHED:
        // PvP turn timer (single has no countdown — confirm when ready).
        if (!this._isLocalMode()) {
          this.timer -= dt;
          if (this.timer <= 0) this._finalizeSched();
        }
        break;
      case GS.EXEC:
        this._updateExec(dt);
        break;
    }
  }

  _refreshAvailSpells(): void {
    // Available "spells" = distinct castable elements in hand (stackable +
    // effect; filter cards are instant, not tick plays). `lv` repurposed as
    // the in-hand copy count. The same element may be played on multiple ticks.
    this.availSpells = [];
    if (!this.playerDeck) return;
    const counts = new Map<number, number>();
    for (const c of this.playerDeck.hand) {
      const def = CARDS[c.cardId];
      if (!def || def.element === undefined) continue; // skip filter cards
      counts.set(def.element, (counts.get(def.element) || 0) + 1);
    }
    for (const [el, n] of counts) this.availSpells.push({ el, lv: n });
    this.availSpells.sort((a, b) => a.el - b.el);
  }

  // ── Card Tactician — 飛行卡片動畫（抽牌 / 出牌，純視覺層）──────────
  /** Renderer 用：此手牌 uid 是否正在抽牌動畫飛行中（飛行期間不畫靜態卡避免重影）。 */
  isCardAnimating(uid: string): boolean {
    for (const c of this.cardFx) if (c.kind === 'draw' && c.uid === uid) return true;
    return false;
  }

  /** 產生抽牌飛行動畫：卡背從牌堆飛入各自手牌槽（翻面 + 淡入 + 放大）。
   *  `uids` = 這次實際抽到手的牌；`origin` 決定飛出起點（牌庫 / 棄牌）。 */
  private _spawnDrawFx(uids: string[], origin: 'deck' | 'discard' = 'deck'): void {
    const deck = this.playerDeck;
    if (!deck || uids.length === 0) return;
    const src = origin === 'discard' ? Renderer.discardPileRect() : Renderer.deckPileRect();
    const sx = src.x + src.w / 2, sy = src.y + src.h / 2;
    let stagger = 0;
    for (const uid of uids) {
      const idx = deck.hand.findIndex((c) => c.uid === uid);
      if (idx < 0) continue; // 滿手 overflow / 找不到 → 不做動畫
      const r = Renderer.cardHandRect(idx, deck.hand.length);
      this.cardFx.push({
        kind: 'draw', cardId: deck.hand[idx].cardId, uid,
        x: sx, y: sy, scale: 0.45, alpha: 0, flip: 0,
        delay: stagger, started: false, dur: 0.34,
        tx: r.x + r.w / 2, ty: r.y + r.h / 2, tscale: 1, talpha: 1,
      });
      stagger += 0.07;
    }
  }

  /** 產生出牌飛行動畫：已排進 tick 的牌從原手牌位置縮進時間軸槽（淡出）。 */
  private _spawnPlayFx(sources: { cardId: string; x: number; y: number }[], slot: number): void {
    if (slot < 0 || slot > 2) return;
    const t = Renderer.cardTickRect(slot);
    const tx = t.x + t.w / 2, ty = t.y + t.h / 2;
    let stagger = 0;
    for (const s of sources) {
      this.cardFx.push({
        kind: 'play', cardId: s.cardId, uid: undefined,
        x: s.x, y: s.y, scale: 1, alpha: 1, flip: 1,
        delay: stagger, started: false, dur: 0.26,
        tx, ty, tscale: 0.32, talpha: 0,
      });
      stagger += 0.05;
    }
    if (slot >= 0 && slot < 3) this.tickFlash[slot] = 1;
  }

  /** 每幀推進飛行卡動畫：delay 到點才 kick off tween；衰減 tick 放置閃光。 */
  private _updateCardFx(dt: number): void {
    for (let i = 0; i < 3; i++) {
      if (this.tickFlash[i] > 0) this.tickFlash[i] = Math.max(0, this.tickFlash[i] - dt * 2.4);
    }
    if (this.cardFx.length === 0) return;
    for (const c of this.cardFx) {
      if (c.started) continue;
      if (c.delay > 0) { c.delay -= dt; continue; }
      c.started = true;
      anim.add(
        c as unknown as Record<string, number>,
        { x: c.tx, y: c.ty, scale: c.tscale, alpha: c.talpha, flip: 1 },
        c.dur, 'easeOut',
        () => { const i = this.cardFx.indexOf(c); if (i >= 0) this.cardFx.splice(i, 1); },
      );
    }
  }

  /** 從 `from` 起找下一個可規劃的 tick（跳過已用來抽牌的 tick）。回傳 0..3（3=全排完）。 */
  private _advanceSlot(from: number): number {
    let i = Math.max(0, from);
    while (i < 3 && this.tickDrawUsed[i]) i++;
    return i;
  }

  /**
   * v2 — 跳過目前 tick，改為「從棄牌堆隨機抽 1 張」。隨機、不限次數（自然被 3 個
   * tick 上限卡住）、選用後**不可反悔**、該 tick 暗掉不能再放 cast。目前僅單機
   * （PvP 需另走 CardOp 同步權威 deck，暫不支援）。
   */
  skipTickDraw(): void {
    if (this.state !== GS.SCHED || !this.cardMode || !this.playerDeck) return;
    if (!this._isLocalMode()) return; // PvP：需 CardOp 同步，暫不支援
    const slot = this.spellSlot;
    if (slot < 0 || slot > 2 || this.tickDrawUsed[slot]) return;
    if (this.playerDeck.discardPile.length === 0) return; // 棄牌堆空 → 無牌可回收
    // 此 tick 若已排 cast，先還牌再改抽。
    if (this.tickCards[slot]?.length) this.cardClearTick(slot);
    const card = this.playerDeck.drawFromDiscard();
    if (!card) return; // 滿手 / 抽不到
    this.tickDrawUsed[slot] = true;
    this.pSpells[slot] = null; // 該 tick = Pass（不施法）
    this._spawnDrawFx([card.uid], 'discard'); // 從棄牌堆飛入手牌
    // 收掉選牌狀態、前進到下一個可用 tick。
    this.cardQueueEl = 0; this.cardQueueUids = [];
    this.selSpellType = 0; this.targeting = false; this.targetPos = null;
    this.spellSlot = this._advanceSlot(slot + 1);
    this.schedStep = 'slot';
    this._refreshAvailSpells();
  }

  /** Card mode — place the current tick's stack queue onto the tapped cell,
   *  discard the played cards, and auto-advance to the next tick. */
  confirmTarget(bx: number, by: number): void {
    if (!this.targeting) return;
    const deck = this.playerDeck;
    const slot = this.spellSlot;
    if (!deck || slot < 0 || slot > 2 || this.cardQueueUids.length === 0) {
      this.targeting = false; this.targetPos = null; return;
    }
    const el = this.cardQueueEl;
    // If this tick already had a play (rare in the sequential flow) → return
    // those cards to hand before overwriting.
    if (this.pSpells[slot] && this.tickCards[slot]?.length) {
      for (const uid of this.tickCards[slot]) deck.returnHeldToHand(uid);
      this.tickCards[slot] = [];
    }
    const count = this.cardQueueUids.length;
    this.pSpells[slot] = buildCast(slot, el, { x: bx, y: by }, count);
    // 出牌動畫：放牌前先記各牌在手牌列的中心座標（holdFromHand 後就離手了）。
    const playSrc: { cardId: string; x: number; y: number }[] = [];
    for (const uid of this.cardQueueUids) {
      const idx = deck.hand.findIndex((c) => c.uid === uid);
      if (idx < 0) continue;
      const r = Renderer.cardHandRect(idx, deck.hand.length);
      playSrc.push({ cardId: deck.hand[idx].cardId, x: r.x + r.w / 2, y: r.y + r.h / 2 });
    }
    // M6 desync fix：cast 牌暫存 heldPile，submit（_finalizeSched）才 commitHeld 消耗，
    // 對齊 server applyCasts 時機；避免 SCHED 中途 reshuffle 雙端不一致。
    for (const uid of this.cardQueueUids) deck.holdFromHand(uid);
    this._spawnPlayFx(playSrc, slot);
    this.tickCards[slot] = [...this.cardQueueUids];
    // Clear the queue + auto-advance to the next tick.
    this.cardQueueEl = 0;
    this.cardQueueUids = [];
    this.selSpellType = 0;
    this.targeting = false;
    this.targetPos = null;
    this.spellSlot = this._advanceSlot(slot + 1); // 跳過已用來抽牌的 tick
    this.schedStep = 'slot';
    this._refreshAvailSpells();
  }

  /** ◀返回: cancel the in-progress stack queue (no card consumed yet). */
  goBackSlot(): void {
    if (this.state !== GS.SCHED) return;
    if (this.schedStep === 'target') {
      this.targeting = false;
      this.targetPos = null;
      this.selSpellType = 0;
      this.schedStep = 'slot';
      this._refreshAvailSpells();
    }
  }

  /**
   * M4c — 戰場格 → iso 螢幕中心（特效座標用）。略抬 ISO_DEPTH 讓浮字/粒子
   * 貼齊抬升後的 iso 單位。取代舊正交公式 `OXB + x*CB + CB/2`。
   */
  _fc(cx: number, cy: number): { x: number; y: number } {
    const c = cellToScreen(cx, cy);
    return { x: c.x, y: c.y - ISO_DEPTH };
  }

  /** 佈陣牌組選擇（M4c）：玩家 / AI 牌組原型 id（DECK_TEMPLATES）。 */
  setPlayerDeck(id: string): void {
    if (getDeckTemplate(id)) this.playerDeckId = id;
  }
  setAiDeck(id: string): void {
    if (getDeckTemplate(id)) this.aiDeckId = id;
  }

  // ── Card Tactician (M4b) — 手牌列 / tick 時間軸出牌互動 ──────────
  /** 該元素是否為可疊加牌（決定同 tick 同目標能否疊加）。 */
  _isStackableEl(el: number): boolean {
    for (const id in CARDS) {
      const d = CARDS[id];
      if (d.element === el) return d.category === 'stackable';
    }
    return false;
  }

  /** 是否有牌在某張手牌的疊加佇列中（Renderer 高亮用）。 */
  isCardQueued(uid: string): boolean {
    return this.cardQueueUids.includes(uid);
  }

  /**
   * Renderer 高亮用：這張手牌現在能不能「連點疊加」到目前佇列。
   * 條件：佇列已啟動、可疊加、同元素、且這張還沒在佇列裡。
   */
  canStackOnQueue(uid: string): boolean {
    if (this.cardQueueUids.length === 0 || !this._isStackableEl(this.cardQueueEl)) return false;
    if (this.cardQueueUids.includes(uid)) return false;
    const card = this.playerDeck?.hand.find((c) => c.uid === uid);
    return !!card && CARDS[card.cardId]?.element === this.cardQueueEl;
  }

  /** 清除某 tick 的出牌：把已出的牌還回手牌。 */
  cardClearTick(tick: number): void {
    if (this.state !== GS.SCHED || !this.cardMode || !this.playerDeck) return;
    if (tick < 0 || tick > 2) return;
    if (this.tickDrawUsed[tick]) return; // 已用來抽牌的 tick 不可還原（隨機抽出、不可反悔）
    for (const uid of this.tickCards[tick] || []) this.playerDeck.returnHeldToHand(uid);
    this.tickCards[tick] = [];
    this.pSpells[tick] = null;
    this._refreshAvailSpells();
  }

  /**
   * Card Tactician (M6) — in PvP, forward a filter CardOp to the server's
   * authoritative deck so it stays byte-identical to our local deck. The
   * client has ALREADY mutated its own deck identically (same draw / tutor /
   * cycle); the server re-applies via `applyCardOp` and validates ownership.
   * No-op in single (LocalServerAdapter manages no authoritative deck).
   */
  private _sendCardOp(op: CardOp): void {
    if (this._isLocalMode() || !this.adapter) return;
    void this.adapter.cardOp(this.myTeam, op).catch((err) => {
      console.warn('[game] cardOp(' + op.kind + ') failed:', (err as Error).message);
    });
  }

  /**
   * 點手牌列的一張牌（M4b 自動推進流程）：
   *   - filter → 立即結算（抽牌；搜牌 / 調度待後續）。
   *   - stackable / effect →
   *       佇列已有同（可疊加）元素 → 連點疊加（加入佇列）；
   *       否則 → 開新佇列（換元素就取代）。
   *     設定 targeting，等玩家點戰場格 confirmTarget 一次放置 + 自動跳下一 tick。
   */
  cardPickCard(handIndex: number): void {
    if (this.state !== GS.SCHED || !this.cardMode || !this.playerDeck) return;
    const card = this.playerDeck.hand[handIndex];
    if (!card) return;
    const def = CARDS[card.cardId];
    if (!def) return;

    // 搜牌（tutor）選牌模式：手牌列點擊忽略（選牌走牌庫選單 tutorPick）。
    if (this.tutorMode) return;

    // 調度（cycle）選棄模式：再點調度＝確認；點其他牌＝標記/取消標記要棄的牌。
    if (this.cycleMode) {
      if (card.uid === this.cycleCardUid) {
        const n = this.cycleMarks.length;
        // M6 — capture discard cardIds BEFORE mutating (the wire op references
        // cardId, not uid). The server re-applies the same cycle on its deck.
        const discardIds = this.cycleMarks
          .map((uid) => this.playerDeck!.hand.find((c) => c.uid === uid)?.cardId)
          .filter((id): id is string => id !== undefined);
        for (const uid of this.cycleMarks) this.playerDeck.discardFromHand(uid);
        this.playerDeck.discardFromHand(card.uid);
        if (n > 0) this.playerDeck.draw(n);
        this._sendCardOp({ kind: 'cycle', spent: card.cardId, discard: discardIds });
        this.cycleMode = false;
        this.cycleCardUid = undefined;
        this.cycleMarks = [];
        this._refreshAvailSpells();
      } else {
        const mi = this.cycleMarks.indexOf(card.uid);
        if (mi >= 0) this.cycleMarks.splice(mi, 1);
        else if (this.cycleMarks.length < this.cycleLimit) this.cycleMarks.push(card.uid);
      }
      return;
    }

    if (def.category === 'filter') {
      if (def.filterKind === 'draw') {
        this.playerDeck.discardFromHand(card.uid);
        const drawn = this.playerDeck.draw(def.filterAmount ?? 2);
        this._sendCardOp({ kind: 'draw', spent: card.cardId });
        this._spawnDrawFx(drawn.map((c) => c.uid));
        this._refreshAvailSpells();
      } else if (def.filterKind === 'cycle') {
        // 進入選棄模式（調度卡先留著，確認時才棄）。
        this.cycleMode = true;
        this.cycleCardUid = card.uid;
        this.cycleMarks = [];
        this.cycleLimit = def.filterAmount ?? 2;
        this.cardQueueEl = 0; this.cardQueueUids = [];
        this.selSpellType = 0; this.targeting = false; this.targetPos = null;
        this.schedStep = 'slot';
      } else if (def.filterKind === 'tutor') {
        // 進入搜牌模式：彈出牌庫選單，選一張上手。
        this.tutorMode = true;
        this.tutorCardUid = card.uid;
        this.cardQueueEl = 0; this.cardQueueUids = [];
        this.selSpellType = 0; this.targeting = false; this.targetPos = null;
        this.schedStep = 'slot';
      }
      return;
    }
    if (this.spellSlot < 0 || this.spellSlot > 2) return; // 三個 tick 都排完 → 按確認 / 返回
    const el = def.element ?? 0;
    // 重複點選「已在疊加佇列中」的牌 → 取消該張疊加（而非重新單選）。
    const qi = this.cardQueueUids.indexOf(card.uid);
    if (qi >= 0) {
      this.cardQueueUids.splice(qi, 1);
      if (this.cardQueueUids.length === 0) {
        // 佇列清空 → 取消選牌，退回選牌步驟。
        this.cardQueueEl = 0;
        this.selSpellType = 0;
        this.targeting = false;
        this.targetPos = null;
        this.schedStep = 'slot';
      }
      return;
    }
    if (this.cardQueueUids.length > 0 && this.cardQueueEl === el && this._isStackableEl(el)) {
      this.cardQueueUids.push(card.uid); // 連點疊加
    } else {
      this.cardQueueEl = el;
      this.cardQueueUids = [card.uid]; // 開新 / 換元素
    }
    this.selSpellType = el;
    this.targeting = true;
    this.schedStep = 'target';
  }

  /**
   * 返回鍵：選牌中 → 取消這次選牌；否則 → 退回上一個 tick 並把牌收回手牌。
   */
  /** 搜牌選單選項：牌庫中各 cardId 與張數（Renderer/Input 用）。 */
  tutorOptions(): { cardId: string; count: number }[] {
    if (!this.playerDeck) return [];
    const m = new Map<string, number>();
    for (const c of this.playerDeck.drawPile) m.set(c.cardId, (m.get(c.cardId) || 0) + 1);
    return [...m.entries()]
      .map(([cardId, count]) => ({ cardId, count }))
      .sort((a, b) => a.cardId.localeCompare(b.cardId));
  }

  /** 搜牌：把牌庫指定 cardId 一張上手，棄掉搜牌卡、結束搜牌模式。 */
  tutorPick(cardId: string): void {
    if (this.state !== GS.SCHED || !this.cardMode || !this.tutorMode || !this.playerDeck) return;
    // M6 — capture the spent tutor card's cardId BEFORE discarding it (the
    // wire op references cardId). Look it up while it's still in hand.
    const spentId =
      (this.tutorCardUid
        ? this.playerDeck.hand.find((c) => c.uid === this.tutorCardUid)?.cardId
        : undefined) ?? 'tutor';
    const got = this.playerDeck.tutor(cardId);
    if (!got) return; // 牌庫沒這張 / 滿手 → 留在選單
    if (this.tutorCardUid) this.playerDeck.discardFromHand(this.tutorCardUid);
    this._sendCardOp({ kind: 'tutor', spent: spentId, want: cardId });
    this._spawnDrawFx([got.uid]); // 搜到的牌從牌庫飛入手牌
    this.tutorMode = false;
    this.tutorCardUid = undefined;
    this._refreshAvailSpells();
  }

  cardBack(): void {
    if (this.state !== GS.SCHED || !this.cardMode) return;
    if (this.tutorMode) {
      // 取消搜牌（搜牌卡留在手上）。
      this.tutorMode = false;
      this.tutorCardUid = undefined;
      return;
    }
    if (this.cycleMode) {
      // 取消調度（不棄牌、不抽牌；調度卡留在手上）。
      this.cycleMode = false;
      this.cycleCardUid = undefined;
      this.cycleMarks = [];
      return;
    }
    if (this.cardQueueUids.length > 0) {
      this.cardQueueEl = 0;
      this.cardQueueUids = [];
      this.selSpellType = 0;
      this.targeting = false;
      this.targetPos = null;
      this.schedStep = 'slot';
      return;
    }
    if (this.spellSlot <= 0) return;
    let t = Math.min(this.spellSlot, 3) - 1;
    while (t >= 0 && this.tickDrawUsed[t]) t--; // 跳過已抽牌（不可反悔）的 tick
    if (t < 0) return;
    this.cardClearTick(t);
    this.spellSlot = t;
    this.schedStep = 'slot';
  }

  /** 跳過鍵：此 tick 不出牌（finalize 補 Pass），直接跳下一個 tick。 */
  cardPassTick(): void {
    if (this.state !== GS.SCHED || !this.cardMode) return;
    this.cardQueueEl = 0;
    this.cardQueueUids = [];
    this.selSpellType = 0;
    this.targeting = false;
    this.targetPos = null;
    if (this.spellSlot >= 0 && this.spellSlot < 3) {
      this.pSpells[this.spellSlot] = null;
      this.spellSlot = this._advanceSlot(this.spellSlot + 1);
    }
    this.schedStep = 'slot';
  }

  /** 卡牌模式的「確認出牌」：直接送出排程（空 tick 自動補 Pass）。 */
  confirmCardSchedule(): void {
    if (this.state !== GS.SCHED || !this.cardMode) return;
    this._finalizeSched();
  }

  private _finalizeSched(): void {
    // Card Tactician (M3) — plan the AI's 3-tick schedule from its deck NOW
    // (board state == player's at confirm time). planAiSchedule also resolves
    // the AI's filter cards (instant) and discards the cards it plays.
    // aiTeam = opponent of myTeam (1 in single-player).
    if (this.cardMode && this.aiDeck) {
      const aiTeam = (this.myTeam === 0 ? 1 : 0) as 0 | 1;
      this.aSpells = planAiSchedule(this.aiDeck, this.units, this.bases, this.terrain, aiTeam);
    }
    for (let i = 0; i < 3; i++) {
      if (!this.pSpells[i]) this.pSpells[i] = { slotIndex: i, spellType: 0, powerLevel: 0 };
      if (!this.aSpells[i]) this.aSpells[i] = { slotIndex: i, spellType: 0, powerLevel: 0 };
    }
    if (this.adapter) {
      // Phase 3d Step 4 fix: in PvP, our pSpells targetPos are in VIEW
      // coords (set by confirmTarget from the user's click). Flip them
      // back to world before sending so server-side spell resolution
      // sees coordinates relative to the authoritative world grid.
      // In single mode, viewFlipped is false so this is identity.
      const myScheduleWorld = this.pSpells.map((c) => this._cmdViewToWorld(c));
      void this.adapter.submitSchedule(this.myTeam, myScheduleWorld).catch((err) => {
        console.warn('[game] submitSchedule(' + this.myTeam + ') failed:', (err as Error).message);
      });
      if (this._isLocalMode()) {
        // aSpells in single/story modes are AI-generated using unit.pos which
        // is also in view coords (== world for myTeam=0), so no flip needed.
        const oppTeam = (this.myTeam === 0 ? 1 : 0) as 0 | 1;
        void this.adapter.submitSchedule(oppTeam, this.aSpells as SpellCommand[]).catch((err) => {
          console.warn('[game] submitSchedule(' + oppTeam + ') failed:', (err as Error).message);
        });
      }
    }
    // M6 desync fix — 本回合 tick 出牌（暫存 heldPile）在此一次併入 discardPile，
    // 對齊 server 於 submitSchedule 的 applyCasts 消耗時機（雙端 deck 同步）。
    this.playerDeck?.commitHeld();
    // _startExec is async; we don't await because update(dt) is the game loop
    // driver. While exec is pending the stepActive flag keeps the frame loop
    // idle — see _startExec / _updateExec below.
    void this._startExec();
  }

  private async _startExec(): Promise<void> {
    this.state = GS.EXEC;
    this.transitionTimer = 0.6;
    this.transitionLabel = 'Battle!';
    this.targeting = false;
    // Session 6c follow-up: default to MATCH3 tab during EXEC so the player
    // sees the board (still dimmed read-only) during the battle animation;
    // they can flip to the 'sched' tab to review the locked-in schedule.
    this.bottomTab = 'match3';
    this.phaseBanner = null;
    anim.clear();
    // anim.clear() 移除所有 tween → 飛行卡的 onDone 不會觸發，殘留物件會凍在原地。
    // SCHED 動畫在進 EXEC 時本就該收乾淨，直接清空。
    this.cardFx = [];
    this.tickFlash = [0, 0, 0];

    // Reset event playback machinery; stepActive stays false until the
    // server returns the event log, so _updateExec won't start playing
    // a stale or empty list.
    this.events = [];
    this.stepIdx = 0;
    this.stepTimer = 0;
    this.stepApplied = false;
    this.stepActive = false;

    if (!this.adapter) return;

    // Phase 3d Step 4: PvP mode does NOT call executeExec. Server gates
    // exec on both schedules and pushes execComplete to both clients.
    // The push handler (installed in startCreateRoom/startJoinRoom)
    // sets this.events + this.stepActive = true. Single + story take
    // the local path below.
    if (!this._isLocalMode()) {
      this.execPending = true;
      this.waitingForOpponent = true;
      return;
    }

    this.execPending = true;
    this.adapterError = null;
    try {
      const res = await this.adapter.executeExec({
        playerSpells: this.pSpells as [SpellCommand, SpellCommand, SpellCommand],
        aiSpells: this.aSpells as [SpellCommand, SpellCommand, SpellCommand],
      });
      this.events = res.events;
    } catch (err) {
      this.adapterError = `executeExec failed: ${(err as Error).message}`;
      console.error('[game]', this.adapterError);
      this.events = [];
    } finally {
      this.execPending = false;
    }
    this.stepActive = true;
  }

  private _updateExec(dt: number): void {
    if (!this.stepActive) return;
    if (this.stepIdx >= this.events.length) {
      this._finishExec(); return;
    }
    const event = this.events[this.stepIdx];

    this.stepTimer += dt;

    if (!this.stepApplied) {
      this.stepApplied = true;
      this._applyEvent(event);
    }

    if (this.stepTimer >= event.dur && !anim.busy) {
      this.stepIdx++;
      this.stepTimer = 0;
      this.stepApplied = false;
    }
  }

  private _applyEvent(event: BattleEvent): void {
    switch (event.type) {
      case 'tickLabel':
        // Session 10 follow-up — tick boundary modifier decrement.
        // Server decrements at end-of-tick (after Step F); the wire
        // doesn't carry an explicit "tick ended" event, so the client
        // uses each tickLabel (tick > 0) as the "previous tick just
        // ended" signal. Final tick's end-of-tick fires in `_finishExec`.
        if (event.tick && event.tick > 0) {
          this._decrementUnitModifiers();
        }
        this.fx.push({
          type: 'floatText', x: W / 2, y: ZBY + OYB + CB * 4,
          text: `⚔ Tick ${event.tick! + 1}`,
          color: '#ffdd44', alpha: 1.5, timer: 1.2, size: 20,
        });
        break;

      case 'spell': {
        // Phase banner — Session 6a split spell into friendly (heal/buff
        // at tick start) vs hostile (damage/debuff at tick end before
        // contest). Sticky banner (Session 6c follow-up): set
        // `phaseBanner` and let it sit until the next sub-step replaces it.
        const phase = (event as { phase?: 'friendly' | 'hostile' }).phase;
        const isFriendly = phase === 'friendly';
        this.phaseBanner = {
          text: isFriendly ? '✦ 輔助魔法 ✦' : '✦ 攻擊魔法 ✦',
          color: isFriendly ? '#44ddaa' : '#ffaa44',
        };
        const spellList = event.spells || [];
        for (const se of spellList) {
          // Apply impacts to units
          for (const impact of se.impacts) {
            const u = this.units.find(x => x.id === impact.unitId);
            if (!u) continue;
            u.totalSoldiers = impact.totalSoldiers;
            if (impact.died) { u.dead = true; u.totalSoldiers = 0; }

            // Session 10 follow-up — mirror server's modifier push on the
            // client so Renderer can draw buff/debuff badges on the unit.
            // Server's authoritative modifier state lives in StateManager;
            // Game.units is a separate copy (cloneState splits them at
            // initBattle), so we have to apply the same push locally to
            // keep the visual list in sync.
            //
            // Look up the matching EffectKind in the spell's effects[] to
            // get its durationTicks (impact wire format only carries the
            // applied VALUE, not the duration).
            //
            // Session 11 — rooted joins the mirror set. `impact.rooted`
            // carries the duration directly (= the modifier's initial
            // ticksRemaining), so we don't need to peek at the spell
            // def for it; the wire ships the number we need.
            //
            // Session 11 follow-up — defDown is mirrored similarly. The
            // impact field carries the per-modifier value; duration is
            // derived from the SpellDef's `defDownDurationTicks` (= 2
            // for hostile Grow). Mutually exclusive with rooted on the
            // same impact (the SpellDef branches per-target).
            const impactKind: 'buffAtk' | 'shieldPhys' | 'shieldMag' | 'rooted' | 'defDown' | null =
                impact.buff !== undefined ? 'buffAtk'
              : impact.defPhys !== undefined ? 'shieldPhys'
              : impact.defMag !== undefined ? 'shieldMag'
              : impact.rooted !== undefined && impact.rooted > 0 ? 'rooted'
              : impact.defDown !== undefined && impact.defDown > 0 ? 'defDown'
              : null;
            if (impactKind) {
              let duration: number;
              let value: number;
              if (impactKind === 'rooted') {
                duration = impact.rooted ?? 1;
                value = 1; // rooted is presence-only; value is ignored by _recomputeScalars
              } else if (impactKind === 'defDown') {
                // growGrass effect bundles defDown — pull the durationTicks
                // from there. Wire carries the per-mod value already.
                const eff = se.sp.effects.find((e) => e.kind === 'growGrass') as
                  | { defDownDurationTicks?: number } | undefined;
                duration = (eff?.defDownDurationTicks ?? 2);
                value = impact.defDown ?? 0;
              } else {
                const eff = se.sp.effects.find((e) => e.kind === impactKind) as
                  | { durationTicks?: number } | undefined;
                duration = (eff?.durationTicks ?? 1);
                value = impactKind === 'buffAtk'
                  ? (impact.buff ?? 0)
                  : impactKind === 'shieldPhys'
                    ? (impact.defPhys ?? 0)
                    : (impact.defMag ?? 0);
              }
              _pushModifier(u, impactKind, value, duration, se.cmd.spellType);
            }
          }
          // Session 9 — whirlwind embeds MoveRecord[] (one per unit
          // actually displaced). Mirror the server's state mutation on
          // the local model AND animate the slide so the player sees
          // the displacement.
          //
          // Session 10 follow-up — KEEP the unit's drawn path. Path is
          // the player's intended route; the unit just gets nudged off
          // it for a tick or two and walks back via _stepToward (see
          // movement.ts priority 2). Auto-advance `pathIdx` to the
          // closest remaining waypoint so units blown PAST waypoints
          // don't backtrack — must mirror server `spell.ts._applyWhirlwind`.
          if (Array.isArray(se.moves) && se.moves.length > 0) {
            for (const m of se.moves) {
              const u = this.units.find((x) => x.id === m.unitId);
              if (!u) continue;
              const dest = { x: m.to.x, y: m.to.y };
              u.pos = dest;
              // Mirror the server's pathIdx auto-advance logic so the
              // client's render-time path preview matches what movement
              // events will deliver next tick.
              let bestIdx = u.pathIdx;
              let bestDist = bestIdx < u.path.length ? md(dest, u.path[bestIdx]) : Infinity;
              for (let i = u.pathIdx + 1; i < u.path.length; i++) {
                const d = md(dest, u.path[i]);
                if (d < bestDist) { bestDist = d; bestIdx = i; }
              }
              while (
                bestIdx < u.path.length &&
                u.path[bestIdx].x === dest.x &&
                u.path[bestIdx].y === dest.y
              ) {
                bestIdx++;
              }
              u.pathIdx = bestIdx;
              // Slide tween — overlap with the whirlwind ring fx.
              anim.add(u as any, { vx: m.to.x, vy: m.to.y }, 0.6, 'easeOut');
              // Light trail line for visibility.
              const fx = this._fc(m.from.x, m.from.y).x;
              const fy = this._fc(m.from.x, m.from.y).y;
              const tx = this._fc(m.to.x, m.to.y).x;
              const ty = this._fc(m.to.x, m.to.y).y;
              const tcol = u.team === this.myTeam ? '#4488ff' : '#ff4444';
              this.fx.push({
                type: 'moveTrail', x1: fx, y1: fy, x2: tx, y2: ty,
                color: tcol, alpha: 0.7, timer: 0.7,
              });
            }
          }
          this._renderSpellFx(se as any);
          // c 段 — fire ignite particles SYNCHRONOUSLY with the fireball
          // animation. The state-mutation `terrainIgnite` event still
          // arrives in the log right after this spell event but doesn't
          // produce visuals (it's silent now). Predicting which cells
          // ignite by scanning the spell radius for current 'grass' on
          // the local terrain is safe because the ignite event hasn't
          // been applied yet — terrain still holds 'grass' here.
          if (
            phase === 'hostile' &&
            this.terrain &&
            ELEM[se.cmd.spellType] === 'Fire'
          ) {
            this._spawnIgniteFx(se.tgt, se.sp.radius);
          }
          // Session 11 (Leader CCC) — Grow / Rain mirror the Fire ignite
          // pattern: paint client-side terrain synchronously so the cell
          // visually flips green/blue AT THE SPELL HIT FRAME, not 0.05s
          // later when the trailing terrainGrow/terrainRain event arrives.
          // The trailing event is idempotent — it only flips cells still
          // showing 'none', so double-painting is a no-op.
          if (
            phase === 'hostile' &&
            this.terrain &&
            ELEM[se.cmd.spellType] === 'Grow'
          ) {
            this._spawnGrowFx(se.tgt, se.sp.radius);
          }
          // Session 12 — Rockfall mirrors Grow/Rain's predictive paint.
          // For each cell in radius (= just the center for radius=0):
          //   - if no unit was on it AT CAST START → predict boulder paint
          //   - if a unit was on it → spawn shatter dust (no boulder)
          // We derive "had a unit at cast start" from `se.impacts` (cells
          // where rockfall actually damaged a unit, including units it
          // killed this cast — which would already show u.dead=true in
          // this.units by the time we render). The trailing
          // terrainBoulderFall event re-asserts the paint authoritatively.
          if (
            phase === 'hostile' &&
            this.terrain &&
            ELEM[se.cmd.spellType] === 'Rockfall'
          ) {
            this._spawnRockfallFx(se);
          }
          // Session 12 — Fire / Lightning shatter boulders in radius. Same
          // predictive pattern: client mutates terrain at the hit frame,
          // trailing terrainBoulderBreak event confirms authoritatively.
          if (
            phase === 'hostile' &&
            this.terrain &&
            (ELEM[se.cmd.spellType] === 'Fire' || ELEM[se.cmd.spellType] === 'Lightning')
          ) {
            this._spawnRockfallBreakFx(se.tgt, se.sp.radius);
          }
          if (
            phase === 'hostile' &&
            this.terrain &&
            ELEM[se.cmd.spellType] === 'Rain'
          ) {
            this._spawnRainFx(se.tgt, se.sp.radius);
            // Session 11 follow-up — 淹水特效：when Rain lands on a target
            // that's standing on a pre-existing puddle (= the "drown bonus"
            // branch), spawn an extra flood burst on that cell. Per-impact
            // so radius-1 hits can mix in/out puddle targets, each getting
            // its own visual confirmation.
            for (const impact of se.impacts) {
              const tgt = this.units.find((x) => x.id === impact.unitId);
              if (!tgt) continue;
              const row = this.terrain[tgt.pos.y];
              if (!row) continue;
              if (row[tgt.pos.x] !== 'puddle') continue;
              this._spawnFloodFx(tgt.pos);
            }
          }
          // fix#5 — Lightning hitting a unit standing in a puddle gets
          // an extra "electrified water" burst on top of the standard
          // lightning fx. Per-target since radius-1 hits can mix in /
          // out puddle (matches the spell.ts +30% rule).
          if (
            phase === 'hostile' &&
            this.terrain &&
            ELEM[se.cmd.spellType] === 'Lightning'
          ) {
            for (const impact of se.impacts) {
              const u = this.units.find((x) => x.id === impact.unitId);
              if (!u) continue;
              const row = this.terrain[u.pos.y];
              if (!row) continue;
              if (row[u.pos.x] !== 'puddle') continue;
              this._spawnPuddleZapFx(u.pos);
            }
          }
        }
        break;
      }

      case 'move': {
        // Phase banner — sticky (Session 6c follow-up)
        this.phaseBanner = { text: '➤ 士兵移動 ➤', color: '#44ccff' };

        // Apply final positions eagerly. event.moves is in chronological
        // order (sub-phase 0 then sub-phase 1), so advancing pathIdx
        // step-by-step here keeps the rendered path preview aligned with
        // the unit's actual progress — Renderer._battlefield draws from
        // pathIdx onward, so unwalked waypoints are the only ones shown.
        for (const m of event.moves) {
          const u = this.units.find(x => x.id === m.unitId);
          if (!u) continue;
          u.pos = { ...m.to };
          if (u.pathIdx < u.path.length) {
            const wp = u.path[u.pathIdx];
            if (wp && wp.x === m.to.x && wp.y === m.to.y) {
              u.pathIdx += 1;
            }
          }
        }
        for (const c of event.contested) {
          const uA = this.units.find(x => x.id === c.unitAId);
          const uB = this.units.find(x => x.id === c.unitBId);
          if (uA) {
            uA.pos = { ...c.pos };
            if (uA.pathIdx < uA.path.length) {
              const wpA = uA.path[uA.pathIdx];
              if (wpA && wpA.x === c.pos.x && wpA.y === c.pos.y) uA.pathIdx += 1;
            }
          }
          if (uB) {
            uB.pos = { ...c.pos };
            if (uB.pathIdx < uB.path.length) {
              const wpB = uB.path[uB.pathIdx];
              if (wpB && wpB.x === c.pos.x && wpB.y === c.pos.y) uB.pathIdx += 1;
            }
          }
        }

        // Build friendly stacking offset map (after apply, pos reflects final positions)
        const stackMap = new Map<string, BattleUnit[]>();
        for (const u of this.units) {
          if (u.dead) continue;
          const key = `${u.team},${u.pos.x},${u.pos.y}`;
          if (!stackMap.has(key)) stackMap.set(key, []);
          stackMap.get(key)!.push(u);
        }
        /** Get visual X offset for a unit due to friendly stacking */
        const getStackOff = (u: BattleUnit): number => {
          const key = `${u.team},${u.pos.x},${u.pos.y}`;
          const group = stackMap.get(key);
          if (!group || group.length <= 1) return 0;
          const idx = group.indexOf(u);
          return (idx - (group.length - 1) / 2) * 0.35;
        };

        // Build set of contested unit IDs for special animation
        const contestedIds = new Set<string>();
        if (event.contested) {
          for (const c of event.contested) {
            contestedIds.add(c.unitAId);
            contestedIds.add(c.unitBId);
          }
        }

        // Group move entries by unit to chain multi-step animations
        const movesByUnit = new Map<string, { unit: BattleUnit; entries: typeof event.moves }>();
        for (const m of event.moves!) {
          const u = this.units.find(x => x.id === m.unitId);
          if (!u) continue;
          if (!movesByUnit.has(m.unitId)) movesByUnit.set(m.unitId, { unit: u, entries: [] });
          movesByUnit.get(m.unitId)!.entries!.push(m);
        }

        // Animate non-contested units (with friendly stacking offset)
        for (const [uid, { unit: u, entries }] of movesByUnit) {
          if (contestedIds.has(uid)) continue; // handled below
          const tcol = u.team === this.myTeam ? '#4488ff' : '#ff4444';
          const sOff = getStackOff(u);
          // Draw trail lines for all steps
          for (const m of entries!) {
            const fx = this._fc(m.from.x, m.from.y).x;
            const fy = this._fc(m.from.x, m.from.y).y;
            const tx = this._fc(m.to.x, m.to.y).x;
            const ty = this._fc(m.to.x, m.to.y).y;
            this.fx.push({
              type: 'moveTrail', x1: fx, y1: fy, x2: tx, y2: ty,
              color: tcol, alpha: 0.8, timer: 0.8,
            });
          }
          // Final arrow
          const last = entries![entries!.length - 1];
          const lfx = this._fc(entries![0].from.x, entries![0].from.y).x;
          const lfy = this._fc(entries![0].from.x, entries![0].from.y).y;
          const ltx = this._fc(last.to.x, last.to.y).x;
          const lty = this._fc(last.to.x, last.to.y).y;
          this.fx.push({
            type: 'moveArrow', x1: lfx, y1: lfy, x2: ltx, y2: lty,
            color: tcol, alpha: 1.0, timer: 0.8,
          });
          // Chain animations with stacking offset on final destination.
          // c 段 / fix#4 — fire water splash at the START of the move
          // that lands on a puddle, so the splash overlaps with the
          // unit's walk-in rather than appearing only after it stops.
          if (entries!.length === 1) {
            const dest = entries![0].to;
            this._splashIfPuddle(dest);
            anim.add(u as any, { vx: dest.x + sOff, vy: dest.y }, 0.7, 'easeOut');
          } else {
            let si = 0;
            const animateNext = () => {
              if (si >= entries!.length) return;
              const m = entries![si];
              si++;
              // Only apply stack offset on the very last step
              const off = si >= entries!.length ? sOff : 0;
              // Splash fires as THIS step begins, so the droplets are
              // visible during the 0.5s walk-in.
              this._splashIfPuddle(m.to);
              anim.add(u as any, { vx: m.to.x + off, vy: m.to.y }, 0.5, 'easeOut', animateNext);
            };
            animateNext();
          }
        }

        // Also animate units that didn't move but may need stacking offset update
        for (const u of this.units) {
          if (u.dead) continue;
          if (movesByUnit.has(u.id)) continue; // already animated
          if (contestedIds.has(u.id)) continue;
          const sOff = getStackOff(u);
          if (Math.abs(u.vx - (u.pos.x + sOff)) > 0.01 || Math.abs(u.vy - u.pos.y) > 0.01) {
            anim.add(u as any, { vx: u.pos.x + sOff, vy: u.pos.y }, 0.4, 'easeOut');
          }
        }

        // Animate contested units — each occupies half the tile
        if (event.contested) {
          for (const c of event.contested) {
            const uA = this.units.find(x => x.id === c.unitAId);
            const uB = this.units.find(x => x.id === c.unitBId);
            if (!uA || !uB) continue;
            // Offset: team 0 left (-0.25), team 1 right (+0.25)
            const offA = uA.team === this.myTeam ? -0.25 : 0.25;
            const offB = uB.team === this.myTeam ? -0.25 : 0.25;
            // Both contesting units land on the same tile — one splash
            // covers both visually, fired as the walk-in starts (fix#4).
            this._splashIfPuddle(c.pos);
            anim.add(uA as any, { vx: c.pos.x + offA, vy: c.pos.y }, 0.7, 'easeOut');
            anim.add(uB as any, { vx: c.pos.x + offB, vy: c.pos.y }, 0.7, 'easeOut');
            // Trail FX for both
            for (const e of [
              { unit: uA, from: c.unitAFrom },
              { unit: uB, from: c.unitBFrom },
            ]) {
              const tcol = e.unit.team === this.myTeam ? '#4488ff' : '#ff4444';
              const fx = this._fc(e.from.x, e.from.y).x;
              const fy = this._fc(e.from.x, e.from.y).y;
              const tx = this._fc(c.pos.x, c.pos.y).x;
              const ty = this._fc(c.pos.x, c.pos.y).y;
              this.fx.push({
                type: 'moveTrail', x1: fx, y1: fy, x2: tx, y2: ty,
                color: tcol, alpha: 0.8, timer: 0.8,
              });
            }
            // Contested indicator — crossed swords
            const cx = this._fc(c.pos.x, c.pos.y).x;
            const cy = this._fc(c.pos.x, c.pos.y).y;
            this.fx.push({
              type: 'floatText', x: cx, y: cy - 16,
              text: '⚔', color: '#ffaa00', alpha: 2.0, timer: 1.5, size: 16,
            });
          }
        }
        break;
      }

      case 'combat': {
        // Phase banner — sticky (Session 6c follow-up)
        this.phaseBanner = { text: '⚔ 士兵攻擊 ⚔', color: '#ff6644' };

        const typeLabels: Record<string, string> = {
          Mage: '法師', Archer: '弓兵', Cavalry: '騎兵', Infantry: '步兵',
        };
        const atkGroups = event.groups;

        // Chain group animations sequentially
        let gi = 0;
        const animateGroup = () => {
          if (gi >= atkGroups.length) return;
          const grp = atkGroups[gi];
          gi++;

          // Apply this type group's damage NOW (synced with animation)
          for (const ud of grp.unitDamages) {
            const u = this.units.find(x => x.id === ud.unitId);
            if (u) {
              u.totalSoldiers = ud.totalSoldiers;
              if (ud.died) { u.dead = true; u.totalSoldiers = 0; }
            }
          }
          for (const bd of grp.baseDamages) {
            const b = this.bases.find(x => x.team === bd.team);
            if (b) {
              b.hp = bd.hp;
              if (bd.destroyed) b.destroyed = true;
            }
          }

          // Type label — Session 6c follow-up: positioned mid-battlefield
          // so it doesn't crash into the sticky phase banner above OYB.
          // Skip when the group is empty (no soldiers of this type
          // attacked this tick).
          if (grp.attacks && grp.attacks.length > 0) {
            this.fx.push({
              type: 'floatText', x: W / 2, y: OYB + CB * (BATTLE_ROWS / 2),
              text: `${typeLabels[grp.groupType]} 攻擊！`,
              color: '#ffcc44', alpha: 1.5, timer: 0.8, size: 13,
            });
          }

          for (const a of grp.attacks!) {
            const attacker = this.units.find(x => x.id === a.attackerId);
            if (!attacker) continue;

            const ax = this._fc(a.attackerPos.x, a.attackerPos.y).x;
            const ay = this._fc(a.attackerPos.x, a.attackerPos.y).y;

            let tx: number, ty: number;
            if (a.targetUnitPos) {
              tx = this._fc(a.targetUnitPos.x, a.targetUnitPos.y).x;
              ty = this._fc(a.targetUnitPos.x, a.targetUnitPos.y).y;
            } else if (a.targetBasePos) {
              tx = this._fc(a.targetBasePos.x, a.targetBasePos.y).x;
              ty = this._fc(a.targetBasePos.x, a.targetBasePos.y).y;
            } else continue;

            const atkCol = attacker.team === this.myTeam ? '#88bbff' : '#ff8888';

            // Attacker lunge animation — dash toward target then snap back
            const lungeX = a.attackerPos.x + (tx - ax) / CB * 0.35;
            const lungeY = a.attackerPos.y + (ty - ay) / CB * 0.35;
            const origX = a.attackerPos.x;
            const origY = a.attackerPos.y;
            anim.add(attacker as any, { vx: lungeX, vy: lungeY }, 0.2, 'easeOut', () => {
              anim.add(attacker as any, { vx: origX, vy: origY }, 0.25, 'bounce');
            });

            // Attacker flash
            attacker.flash = 0.3;
            attacker.flashColor = atkCol;

            // Slash line
            this.fx.push({
              type: 'slash', x1: ax, y1: ay, x2: tx, y2: ty,
              color: atkCol, alpha: 1.0, timer: 0.8,
            });
            // Damage number
            this.fx.push({
              type: 'floatText', x: tx + (Math.random() - 0.5) * 10, y: ty - 12,
              text: '-' + a.dmg, color: '#ff6644', alpha: 1.8, timer: 1.2, size: 13,
            });
            // Hit particles
            for (let p = 0; p < 4; p++) {
              this.fx.push({
                type: 'particle', x: tx, y: ty,
                dx: (Math.random() - 0.5) * 120,
                dy: -30 - Math.random() * 60,
                color: atkCol, alpha: 1.2, timer: 0.8, size: 3,
              });
            }

            if (a.targetUnitId) {
              const target = this.units.find(x => x.id === a.targetUnitId);
              if (target) {
                target.flash = 0.35;
                target.flashColor = '#ff4444';
                if (a.targetDied) {
                  this.fx.push({
                    type: 'deathX', x: tx, y: ty,
                    color: '#ff2222', alpha: 1.5, timer: 1.8, size: CB * 0.4, scale: 1,
                  });
                  for (let p = 0; p < 8; p++) {
                    this.fx.push({
                      type: 'particle', x: tx, y: ty,
                      dx: (Math.random() - 0.5) * 180,
                      dy: -50 - Math.random() * 80,
                      color: target.team === this.myTeam ? '#4488ff' : '#ff4455',
                      alpha: 1.5, timer: 0.8, size: 4,
                    });
                  }
                }
              }
            }

            if (a.baseDied) {
              const targetBase = this.bases.find(x => x.team === a.targetBaseTeam);
              for (let p = 0; p < 12; p++) {
                const angle = (p / 12) * Math.PI * 2;
                this.fx.push({
                  type: 'particle', x: tx, y: ty,
                  dx: Math.cos(angle) * 140,
                  dy: Math.sin(angle) * 140,
                  color: targetBase && targetBase.team === this.myTeam ? '#4488ff' : '#ff4455',
                  alpha: 2.0, timer: 1.2, size: 5,
                });
              }
              this.fx.push({
                type: 'ring', x: tx, y: ty, radius: 5,
                color: '#ff8800', alpha: 2.0, timer: 1.5,
              });
            }
          }

          // Sentinel tween to chain next group after this group's lunge/bounce completes
          const sentinel = { _t: 0 };
          anim.add(sentinel as any, { _t: 1 }, 0.55, 'linear', animateGroup);
        };
        animateGroup();
        break;
      }

      case 'contestResolve': {
        // Phase banner — sticky (Session 6c follow-up)
        this.phaseBanner = { text: '⚔ 爭奪結算 ⚔', color: '#ffaa44' };
        
        // Apply final positions eagerly
        if (event.retreats) {
          for (const r of event.retreats) {
            const u = this.units.find(x => x.id === r.unitId);
            if (u) u.pos = { ...r.to };
          }
        }
        if (event.stays) {
          for (const s of event.stays) {
            const u = this.units.find(x => x.id === s.unitId);
            if (u) u.pos = { ...s.pos };
          }
        }

        // Build stacking offset map for retreat destinations
        const crStackMap = new Map<string, BattleUnit[]>();
        for (const u of this.units) {
          if (u.dead) continue;
          const key = `${u.team},${u.pos.x},${u.pos.y}`;
          if (!crStackMap.has(key)) crStackMap.set(key, []);
          crStackMap.get(key)!.push(u);
        }
        const crStackOff = (u: BattleUnit): number => {
          const key = `${u.team},${u.pos.x},${u.pos.y}`;
          const group = crStackMap.get(key);
          if (!group || group.length <= 1) return 0;
          const idx = group.indexOf(u);
          return (idx - (group.length - 1) / 2) * 0.35;
        };

        // Animate retreating units back to their previous position
        if (event.retreats) {
          for (const r of event.retreats) {
            const u = this.units.find(x => x.id === r.unitId);
            if (!u) continue;
            const rOff = crStackOff(u);
            anim.add(u as any, { vx: r.to.x + rOff, vy: r.to.y }, 0.6, 'easeOut');
            // Retreat trail
            const fx = this._fc(r.from.x, r.from.y).x;
            const fy = this._fc(r.from.x, r.from.y).y;
            const tx = this._fc(r.to.x, r.to.y).x;
            const ty = this._fc(r.to.x, r.to.y).y;
            const tcol = u.team === this.myTeam ? '#4488ff' : '#ff4444';
            this.fx.push({
              type: 'moveTrail', x1: fx, y1: fy, x2: tx, y2: ty,
              color: tcol, alpha: 0.6, timer: 0.6,
            });
            // "Retreat" label
            this.fx.push({
              type: 'floatText', x: tx, y: ty - 12,
              text: '撤退', color: '#ff8844', alpha: 1.5, timer: 1.0, size: 11,
            });
          }
        }

        // Animate staying units to tile center (from half-tile offset)
        if (event.stays) {
          for (const s of event.stays) {
            const u = this.units.find(x => x.id === s.unitId);
            if (u) anim.add(u as any, { vx: s.pos.x, vy: s.pos.y }, 0.4, 'easeOut');
          }
        }
        break;
      }

      case 'terrainIgnite': {
        // a-2 / c 段 / fix#3v2 — IDEMPOTENT SAFETY NET. The grass→
        // burningGrass mutation now happens synchronously inside the
        // preceding `spell` event (so the cell flips at the fireball
        // explosion frame, not after). This event arrives next with the
        // authoritative server-side cell list; for any cell still
        // showing as 'grass' we flip it (covers edge cases where the
        // client's prediction missed something, e.g. server-only
        // terrain drift). Cells already burning are silently ignored.
        if (this.terrain) {
          for (const c of event.cells) {
            if (c.y < 0 || c.y >= this.terrain.length) continue;
            const row = this.terrain[c.y];
            if (!row || c.x < 0 || c.x >= row.length) continue;
            if (row[c.x] === 'grass') row[c.x] = 'burningGrass';
          }
        }
        break;
      }

      case 'terrainGrow': {
        // Session 11 — authoritative confirmation of Grow's terrain
        // transitions. Each cell carries its `to` (destination terrain)
        // so we set unconditionally — the client-side predictor in
        // `_spawnGrowFx` may have already applied the same transition,
        // in which case this is a no-op write of the same value. If
        // the client predicted DIFFERENTLY (e.g. view-flip drift, or
        // future bugs in the transition table), the server's `to`
        // wins because it's authoritative.
        //
        // Session 11 (swamp/base-fix) — transitions are asymmetric so we
        // can NO LONGER replay the transition table client-side here
        // (e.g. server says 'puddle → swamp' but if the client already
        // turned the puddle into swamp, replaying would push swamp →
        // grass — wrong). The `to` field eliminates that ambiguity.
        if (this.terrain) {
          for (const c of event.cells) {
            if (c.y < 0 || c.y >= this.terrain.length) continue;
            const row = this.terrain[c.y];
            if (!row || c.x < 0 || c.x >= row.length) continue;
            row[c.x] = c.to;
          }
        }
        break;
      }

      case 'terrainRain': {
        // Session 11 — same idempotent-via-`to` pattern as terrainGrow.
        if (this.terrain) {
          for (const c of event.cells) {
            if (c.y < 0 || c.y >= this.terrain.length) continue;
            const row = this.terrain[c.y];
            if (!row || c.x < 0 || c.x >= row.length) continue;
            row[c.x] = c.to;
          }
        }
        break;
      }

      case 'terrainBoulderFall': {
        // Session 12 (Leader CCC, Rockfall) — authoritative confirmation
        // that one or more cells now hold a boulder. Client `_spawnRockfallFx`
        // already predicted the paint at the spell-hit frame; this event
        // re-applies unconditionally for any cells the prediction missed
        // (e.g. server-only edge cases). Boulder paint is unconditional —
        // we don't gate on the previous terrain.
        if (this.terrain) {
          for (const c of event.cells) {
            if (c.y < 0 || c.y >= this.terrain.length) continue;
            const row = this.terrain[c.y];
            if (!row || c.x < 0 || c.x >= row.length) continue;
            row[c.x] = 'boulder';
          }
        }
        break;
      }

      case 'terrainBoulderBreak': {
        // Session 12 — Fire / Lightning shattered these boulders back to
        // 'none'. Client may have already predicted this inside the
        // preceding `spell` event (_spawnRockfallBreakFx); this is the
        // safety-net authoritative apply.
        if (this.terrain) {
          for (const c of event.cells) {
            if (c.y < 0 || c.y >= this.terrain.length) continue;
            const row = this.terrain[c.y];
            if (!row || c.x < 0 || c.x >= row.length) continue;
            if (row[c.x] === 'boulder') row[c.x] = 'none';
          }
        }
        break;
      }

      case 'terrainDot': {
        // a-2 — Step F: each unit standing on burningGrass takes
        // ceil(maxSoldiers × 0.15) reduced by tickDefMag. The applies list
        // tells us exact post-damage state, so we just sync the model and
        // pop a floatText for each victim.
        this.phaseBanner = { text: '🔥 地形灼燒 🔥', color: '#ff7733' };
        for (const a of event.applies) {
          const u = this.units.find((x) => x.id === a.unitId);
          if (!u) continue;
          u.totalSoldiers = a.totalSoldiers;
          if (a.died) { u.dead = true; u.totalSoldiers = 0; }
          if (a.dmg > 0) {
            const cx = this._fc(u.pos.x, u.pos.y).x;
            const cy = this._fc(u.pos.x, u.pos.y).y;
            this.fx.push({
              type: 'floatText', x: cx, y: cy - 8,
              text: `-${a.dmg}`, color: '#ff7733',
              alpha: 1.6, timer: 1.0, size: 14,
            });
          }
        }
        break;
      }

      case 'gameEnd':
        break;
    }
  }

  /**
   * c 段 / fix#3v2 — spawn ignition fx AND mutate terrain for every
   * `'grass'` cell within Manhattan `radius` of `tgt` (view coords).
   * Called from the `spell` event handler so the cell visually flips
   * from grass to burningGrass AT THE MOMENT THE FIREBALL HITS, not
   * 2.2s later when the trailing `terrainIgnite` event fires.
   *
   * Mutation is safe to do client-side here because the client's local
   * `terrain` already mirrors the server's authoritative grid (same
   * generateTerrain seed). The trailing `terrainIgnite` event still
   * arrives but is idempotent — re-checks for `'grass'`, so flipping
   * a cell twice does nothing.
   */
  private _spawnIgniteFx(tgt: Pos, radius: number): void {
    if (!this.terrain) return;
    const FLAME_COLORS = ['#ff5522', '#ff8833', '#ffcc44', '#ffeb88'];
    for (let dy = -radius; dy <= radius; dy++) {
      const remX = radius - Math.abs(dy);
      for (let dx = -remX; dx <= remX; dx++) {
        const x = tgt.x + dx;
        const y = tgt.y + dy;
        if (y < 0 || y >= this.terrain.length) continue;
        const row = this.terrain[y];
        if (!row || x < 0 || x >= row.length) continue;
        if (row[x] !== 'grass') continue;
        // Flip the cell immediately so the green→red-orange transition
        // overlaps the fireball explosion frame.
        row[x] = 'burningGrass';
        const cx = this._fc(x, y).x;
        const cy = this._fc(x, y).y;
        this.fx.push({
          type: 'ring', x: cx, y: cy, radius: 2, alpha: 2.0,
          color: '#ffeb88', timer: 0.30,
        });
        this.fx.push({
          type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.6,
          color: '#ff8833', timer: 0.55,
        });
        this.fx.push({
          type: 'ring', x: cx, y: cy, radius: 5, alpha: 1.5,
          color: '#cc4422', timer: 0.70,
        });
        for (let p = 0; p < 10; p++) {
          const ang = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI;
          const speed = 35 + Math.random() * 60;
          this.fx.push({
            type: 'particle',
            x: cx + (Math.random() - 0.5) * CB * 0.4,
            y: cy + (Math.random() - 0.5) * CB * 0.3,
            dx: Math.cos(ang) * speed,
            dy: Math.sin(ang) * speed,
            color: FLAME_COLORS[Math.floor(Math.random() * FLAME_COLORS.length)],
            alpha: 1.6,
            timer: 0.45 + Math.random() * 0.3,
            size: 1.5 + Math.random() * 2.5,
          });
        }
      }
    }
  }

  /**
   * Session 11 (Leader CCC) — spawn growth fx AND mutate terrain for every
   * cell within Manhattan `radius` of `tgt` (view coords) that the
   * server-side `growTransition` says will change. Client predicts the
   * paint synchronously with the spell hit frame; the trailing
   * `terrainGrow` event ships per-cell `to` to overwrite authoritatively.
   *
   * Session 11 (swamp/base-fix) — now branches by destination so:
   *   - 'none'  → 'grass'  : sprout particles
   *   - 'puddle'→ 'swamp'  : muddy-brown burst (water + vegetation)
   *   - 'swamp' → 'grass'  : sprout particles (vegetation wins)
   * Base tiles are skipped — matches server's _applyTerrainTransition.
   */
  private _spawnGrowFx(tgt: Pos, radius: number): void {
    if (!this.terrain) return;
    // Build base-tile lookup from this.bases (already in view coords).
    const baseTiles = new Set<string>();
    for (const b of this.bases) {
      for (const t of b.tiles) baseTiles.add(`${t.x},${t.y}`);
    }
    for (let dy = -radius; dy <= radius; dy++) {
      const remX = radius - Math.abs(dy);
      for (let dx = -remX; dx <= remX; dx++) {
        const x = tgt.x + dx;
        const y = tgt.y + dy;
        if (y < 0 || y >= this.terrain.length) continue;
        const row = this.terrain[y];
        if (!row || x < 0 || x >= row.length) continue;
        if (baseTiles.has(`${x},${y}`)) continue;
        const cur = row[x] as TerrainCell;
        const next = growTransition(cur);
        if (next === null || next === cur) continue;
        // Predict: write the destination now so terrain reads (cell pickers,
        // path overlays, etc) see the new state during the EXEC playback
        // gap. Trailing terrainGrow event will overwrite to the same value.
        row[x] = next;
        this._spawnGrowCellFx(x, y, cur, next);
      }
    }
  }

  /**
   * Per-cell visual for a Grow transition. Split out so terrainGrow event
   * could also spawn fx if needed in the future. Visuals branch on the
   * destination terrain: grass = sprout, swamp = mud burst.
   */
  private _spawnGrowCellFx(x: number, y: number, _from: TerrainCell, to: TerrainCell): void {
    const cx = this._fc(x, y).x;
    const cy = this._fc(x, y).y;
    if (to === 'grass') {
      const SPROUT_COLORS = ['#aaff66', '#88dd44', '#669933', '#ddff88'];
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 2, alpha: 1.8, color: '#ddff88', timer: 0.30 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4, alpha: 1.4, color: '#88dd44', timer: 0.55 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 6, alpha: 1.0, color: '#669933', timer: 0.80 });
      for (let p = 0; p < 8; p++) {
        const ang = -Math.PI / 2 + (Math.random() - 0.5) * 0.8;
        const speed = 30 + Math.random() * 45;
        this.fx.push({
          type: 'particle',
          x: cx + (Math.random() - 0.5) * CB * 0.5,
          y: cy + CB * 0.3,
          dx: Math.cos(ang) * speed,
          dy: Math.sin(ang) * speed,
          color: SPROUT_COLORS[Math.floor(Math.random() * SPROUT_COLORS.length)],
          alpha: 1.5, timer: 0.50 + Math.random() * 0.25, size: 1.5 + Math.random() * 2,
        });
      }
    } else if (to === 'swamp') {
      // Muddy burst — brown/olive palette to suggest "vegetation meets water"
      const MUD_COLORS = ['#6b5a2e', '#88a052', '#3d4a1f', '#aac070'];
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.5, color: '#88a052', timer: 0.35 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 6, alpha: 1.1, color: '#6b5a2e', timer: 0.65 });
      // Splat particles in all directions, slow + heavy (suggesting mud)
      for (let p = 0; p < 10; p++) {
        const ang = Math.random() * Math.PI * 2;
        const speed = 20 + Math.random() * 35;
        this.fx.push({
          type: 'particle',
          x: cx + (Math.random() - 0.5) * CB * 0.3,
          y: cy + (Math.random() - 0.5) * CB * 0.2,
          dx: Math.cos(ang) * speed,
          dy: Math.sin(ang) * speed * 0.6,
          color: MUD_COLORS[Math.floor(Math.random() * MUD_COLORS.length)],
          alpha: 1.4, timer: 0.65 + Math.random() * 0.30, size: 2 + Math.random() * 2,
        });
      }
    }
  }

  /**
   * Session 11 (Leader CCC) — spawn rain fx AND mutate terrain for every
   * cell within Manhattan `radius` of `tgt` (view coords) that the
   * server-side `rainTransition` says will change. Mirrors `_spawnGrowFx`.
   *
   * Session 11 (swamp/base-fix) — branches by destination:
   *   - 'none'  → 'puddle' : raindrop ripples
   *   - 'grass' → 'swamp'  : muddy splatter (vegetation + water)
   *   - 'swamp' → 'puddle' : raindrop ripples (water wins)
   * Base tiles are skipped.
   */
  private _spawnRainFx(tgt: Pos, radius: number): void {
    if (!this.terrain) return;
    const baseTiles = new Set<string>();
    for (const b of this.bases) {
      for (const t of b.tiles) baseTiles.add(`${t.x},${t.y}`);
    }
    for (let dy = -radius; dy <= radius; dy++) {
      const remX = radius - Math.abs(dy);
      for (let dx = -remX; dx <= remX; dx++) {
        const x = tgt.x + dx;
        const y = tgt.y + dy;
        if (y < 0 || y >= this.terrain.length) continue;
        const row = this.terrain[y];
        if (!row || x < 0 || x >= row.length) continue;
        if (baseTiles.has(`${x},${y}`)) continue;
        const cur = row[x] as TerrainCell;
        const next = rainTransition(cur);
        if (next === null || next === cur) continue;
        row[x] = next;
        this._spawnRainCellFx(x, y, cur, next);
      }
    }
  }

  /**
   * Per-cell visual for a Rain transition. Branches on destination.
   * Session 11 follow-up — added `'none'` branch for the
   * `burningGrass → none` extinguish case (steam burst).
   */
  private _spawnRainCellFx(x: number, y: number, from: TerrainCell, to: TerrainCell): void {
    const cx = this._fc(x, y).x;
    const cy = this._fc(x, y).y;
    if (to === 'none' && from === 'burningGrass') {
      // 蒸汽噴發 — rain extinguishes the fire. Hissing white steam puff +
      // a couple of dying ember sparks fading downward as the flames die.
      const STEAM_COLORS = ['#ffffff', '#dddddd', '#cccccc', '#eef0f5'];
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4, alpha: 1.6, color: '#ffffff', timer: 0.35 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 10, alpha: 1.0, color: '#cccccc', timer: 0.65 });
      // Steam cloud — particles drift upward + outward (lighter than air)
      for (let p = 0; p < 12; p++) {
        const ang = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
        const speed = 30 + Math.random() * 45;
        this.fx.push({
          type: 'particle',
          x: cx + (Math.random() - 0.5) * CB * 0.4,
          y: cy + CB * 0.15,
          dx: Math.cos(ang) * speed * 0.6,
          dy: Math.sin(ang) * speed,
          color: STEAM_COLORS[Math.floor(Math.random() * STEAM_COLORS.length)],
          alpha: 1.4,
          timer: 0.70 + Math.random() * 0.40,
          size: 2.5 + Math.random() * 2.5,
        });
      }
      // 4 dying embers falling — fire being defeated
      for (let p = 0; p < 4; p++) {
        this.fx.push({
          type: 'particle',
          x: cx + (Math.random() - 0.5) * CB * 0.3,
          y: cy + (Math.random() - 0.5) * CB * 0.2,
          dx: (Math.random() - 0.5) * 30,
          dy: 25 + Math.random() * 35,
          color: Math.random() > 0.5 ? '#cc4422' : '#995533',
          alpha: 1.2,
          timer: 0.45 + Math.random() * 0.25,
          size: 1.5 + Math.random() * 1.5,
        });
      }
      return;
    }
    if (to === 'puddle') {
      const DROP_COLORS = ['#88ccee', '#66aadd', '#aaddee', '#ffffff'];
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 2, alpha: 1.6, color: '#aaddff', timer: 0.30 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 5, alpha: 1.2, color: '#66aadd', timer: 0.55 });
      for (let p = 0; p < 8; p++) {
        const startX = cx + (Math.random() - 0.5) * CB * 0.7;
        const startY = cy - CB * (0.8 + Math.random() * 0.5);
        this.fx.push({
          type: 'particle',
          x: startX, y: startY,
          dx: (Math.random() - 0.5) * 8,
          dy: 130 + Math.random() * 60,
          color: DROP_COLORS[Math.floor(Math.random() * DROP_COLORS.length)],
          alpha: 1.4, timer: 0.45 + Math.random() * 0.2, size: 1.3 + Math.random() * 1.5,
        });
      }
    } else if (to === 'swamp') {
      // Muddy splatter — same palette as the Grow→swamp variant for
      // visual continuity; both Rain-on-grass and Grow-on-puddle land
      // on the same "this is now swamp" affordance.
      const MUD_COLORS = ['#6b5a2e', '#88a052', '#3d4a1f', '#aac070'];
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.5, color: '#88a052', timer: 0.35 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 6, alpha: 1.1, color: '#6b5a2e', timer: 0.65 });
      for (let p = 0; p < 10; p++) {
        const ang = Math.random() * Math.PI * 2;
        const speed = 20 + Math.random() * 35;
        this.fx.push({
          type: 'particle',
          x: cx + (Math.random() - 0.5) * CB * 0.3,
          y: cy + (Math.random() - 0.5) * CB * 0.2,
          dx: Math.cos(ang) * speed,
          dy: Math.sin(ang) * speed * 0.6,
          color: MUD_COLORS[Math.floor(Math.random() * MUD_COLORS.length)],
          alpha: 1.4, timer: 0.65 + Math.random() * 0.30, size: 2 + Math.random() * 2,
        });
      }
    }
  }

  /**
   * Session 12 (Leader CCC, Rockfall) — predictive paint + falling-rock
   * visual. For each cell in `radius` of `tgt` (view coords), check the
   * local terrain and unit list:
   *   - If no alive unit on the cell, mirror the server's
   *     `_applyBoulderFall`: paint a 'boulder' on the cell now (so it
   *     visually appears at the spell-hit frame) and spawn dust + a
   *     dropping rock streak fx.
   *   - If an alive unit is on the cell, spawn a "shatter" burst —
   *     stone shards + impact ring + a brief dust cloud — without
   *     mutating terrain (the rock broke).
   * Base tiles are skipped — matches the server's defensive base guard.
   *
   * Trailing `terrainBoulderFall` re-asserts the paint authoritatively.
   */
  private _spawnRockfallFx(se: SpellCastRecord): void {
    if (!this.terrain) return;
    const tgt = se.tgt;
    const radius = se.sp.radius;
    const baseTiles = new Set<string>();
    for (const b of this.bases) {
      for (const t of b.tiles) baseTiles.add(`${t.x},${t.y}`);
    }
    // Session 12 follow-up — "had a unit at cast start" comes from the
    // impacts list. Each impact tells us a unit was on a Rockfall-
    // targeted cell (even if it just died). We look up the unit's
    // current pos in this.units, which is unchanged by death (only
    // totalSoldiers / dead flip), so it still gives the cell where the
    // rock shattered. Cells in this set DO NOT get a boulder.
    const shatteredCells = new Set<string>();
    for (const impact of se.impacts) {
      const u = this.units.find((x) => x.id === impact.unitId);
      if (!u) continue;
      shatteredCells.add(`${u.pos.x},${u.pos.y}`);
    }
    for (let dy = -radius; dy <= radius; dy++) {
      const remX = radius - Math.abs(dy);
      for (let dx = -remX; dx <= remX; dx++) {
        const x = tgt.x + dx;
        const y = tgt.y + dy;
        if (y < 0 || y >= this.terrain.length) continue;
        const row = this.terrain[y];
        if (!row || x < 0 || x >= row.length) continue;
        if (baseTiles.has(`${x},${y}`)) continue;
        const cx = this._fc(x, y).x;
        const cy = this._fc(x, y).y;
        const isShattered = shatteredCells.has(`${x},${y}`);

        // Falling-rock streak — always plays so the player sees "stuff
        // dropped here". Heavy dark particles falling from above.
        for (let p = 0; p < 4; p++) {
          this.fx.push({
            type: 'particle',
            x: cx + (Math.random() - 0.5) * CB * 0.4,
            y: cy - CB * (1.4 + Math.random() * 0.6),
            dx: 0,
            dy: 200 + Math.random() * 80,
            color: Math.random() > 0.5 ? '#7a6650' : '#5a4730',
            alpha: 1.5, timer: 0.40, size: 3 + Math.random() * 1.5,
          });
        }

        if (isShattered) {
          // Shatter: stone shards fanning out + impact ring + dust cloud.
          // No terrain mutation — the rock broke on the standing unit.
          const SHARD_COLORS = ['#9a7a55', '#7a6650', '#5a4730', '#c8b395'];
          this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.8, color: '#c8b395', timer: 0.35 });
          this.fx.push({ type: 'ring', x: cx, y: cy, radius: 7, alpha: 1.3, color: '#7a6650', timer: 0.60 });
          this.fx.push({ type: 'ring', x: cx, y: cy, radius: 12, alpha: 0.8, color: '#5a4730', timer: 0.85 });
          for (let p = 0; p < 14; p++) {
            const ang = Math.random() * Math.PI * 2;
            const speed = 60 + Math.random() * 70;
            this.fx.push({
              type: 'particle',
              x: cx + (Math.random() - 0.5) * CB * 0.3,
              y: cy + (Math.random() - 0.5) * CB * 0.2,
              dx: Math.cos(ang) * speed,
              dy: Math.sin(ang) * speed * 0.7,
              color: SHARD_COLORS[Math.floor(Math.random() * SHARD_COLORS.length)],
              alpha: 1.5, timer: 0.55 + Math.random() * 0.30, size: 2 + Math.random() * 2.5,
            });
          }
        } else {
          // Empty cell: paint boulder NOW (client prediction). Settle
          // dust + small ring as the rock thuds into place.
          row[x] = 'boulder';
          this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4, alpha: 1.3, color: '#7a6650', timer: 0.50 });
          this.fx.push({ type: 'ring', x: cx, y: cy, radius: 8, alpha: 0.7, color: '#5a4730', timer: 0.80 });
          for (let p = 0; p < 8; p++) {
            const ang = Math.PI * (0.85 + Math.random() * 0.3); // mostly outward + downward
            const speed = 25 + Math.random() * 35;
            this.fx.push({
              type: 'particle',
              x: cx + (Math.random() - 0.5) * CB * 0.5,
              y: cy + CB * 0.15,
              dx: Math.cos(ang) * speed,
              dy: -Math.sin(ang) * speed * 0.5,
              color: Math.random() > 0.5 ? '#9a8868' : '#6b5640',
              alpha: 1.2, timer: 0.55 + Math.random() * 0.20, size: 2 + Math.random() * 1.5,
            });
          }
        }
      }
    }
  }

  /**
   * Session 12 (Leader CCC counter) — predictive boulder-shatter visual
   * for Fire / Lightning damage with `breakBoulder: true`. For each
   * 'boulder' cell in radius, mutate terrain back to 'none' now (so the
   * cell visually clears at the spell-hit frame) and spawn dust + shards.
   * Trailing `terrainBoulderBreak` event re-asserts authoritatively.
   */
  private _spawnRockfallBreakFx(tgt: Pos, radius: number): void {
    if (!this.terrain) return;
    const SHARD_COLORS = ['#9a7a55', '#7a6650', '#c8b395', '#ddd0b0'];
    for (let dy = -radius; dy <= radius; dy++) {
      const remX = radius - Math.abs(dy);
      for (let dx = -remX; dx <= remX; dx++) {
        const x = tgt.x + dx;
        const y = tgt.y + dy;
        if (y < 0 || y >= this.terrain.length) continue;
        const row = this.terrain[y];
        if (!row || x < 0 || x >= row.length) continue;
        if (row[x] !== 'boulder') continue;
        row[x] = 'none';
        const cx = this._fc(x, y).x;
        const cy = this._fc(x, y).y;
        this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.7, color: '#ffe0a0', timer: 0.35 });
        this.fx.push({ type: 'ring', x: cx, y: cy, radius: 8, alpha: 1.1, color: '#c8b395', timer: 0.65 });
        for (let p = 0; p < 12; p++) {
          const ang = Math.random() * Math.PI * 2;
          const speed = 50 + Math.random() * 60;
          this.fx.push({
            type: 'particle',
            x: cx + (Math.random() - 0.5) * CB * 0.3,
            y: cy + (Math.random() - 0.5) * CB * 0.2,
            dx: Math.cos(ang) * speed,
            dy: Math.sin(ang) * speed,
            color: SHARD_COLORS[Math.floor(Math.random() * SHARD_COLORS.length)],
            alpha: 1.4, timer: 0.50 + Math.random() * 0.30, size: 2 + Math.random() * 2,
          });
        }
      }
    }
  }

  /**
   * Session 11 follow-up — 淹水特效：Rain hits a unit already on a puddle.
   * Spawn a dramatic flood burst on top of the standard rain fx — bigger
   * concentric ripples, splashing droplets fanning outward, and a deep
   * blue ground tint. Stacks ON TOP of `_spawnRainFx`'s per-cell drizzle.
   */
  private _spawnFloodFx(pos: Pos): void {
    const cx = this._fc(pos.x, pos.y).x;
    const cy = this._fc(pos.x, pos.y).y;
    // Three concentric ripples — wider than the regular rain rings to read
    // as "flood, not drizzle". Inner is bright/cyan, outer is deep navy.
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 4, alpha: 2.0,
      color: '#ddf5ff', timer: 0.35,
    });
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 9, alpha: 1.6,
      color: '#66bbff', timer: 0.65,
    });
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 16, alpha: 1.2,
      color: '#3377cc', timer: 0.95,
    });
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 24, alpha: 0.7,
      color: '#1a4488', timer: 1.20,
    });
    // 12 splashing droplets fanning out along the ground plane (vy ≈ 0).
    const SPLASH_COLORS = ['#aaeeff', '#66aadd', '#ffffff', '#3388cc'];
    for (let p = 0; p < 12; p++) {
      const ang = (p / 12) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
      const speed = 70 + Math.random() * 55;
      this.fx.push({
        type: 'particle',
        x: cx + (Math.random() - 0.5) * CB * 0.25,
        y: cy + (Math.random() - 0.5) * CB * 0.25,
        dx: Math.cos(ang) * speed,
        dy: Math.sin(ang) * speed * 0.45 - 18,  // mostly horizontal, slight upward
        color: SPLASH_COLORS[Math.floor(Math.random() * SPLASH_COLORS.length)],
        alpha: 1.6,
        timer: 0.55 + Math.random() * 0.30,
        size: 2 + Math.random() * 2,
      });
    }
    // Extra heavy raindrops from above (denser than _spawnRainFx).
    for (let p = 0; p < 6; p++) {
      const startX = cx + (Math.random() - 0.5) * CB * 0.9;
      const startY = cy - CB * (1.0 + Math.random() * 0.5);
      this.fx.push({
        type: 'particle',
        x: startX, y: startY,
        dx: (Math.random() - 0.5) * 12,
        dy: 170 + Math.random() * 70,
        color: '#88ccee',
        alpha: 1.5,
        timer: 0.45 + Math.random() * 0.20,
        size: 1.8 + Math.random() * 1.6,
      });
    }
  }

  /**
   * Session 11 follow-up — 纏繞特效：Grow lands on a unit already standing
   * on grass (= the "root" branch). Spawn vines wrapping around the unit,
   * green tendril particles spiraling inward. Visually distinct from
   * `_spawnGrowFx`'s upward sprouts so the player can tell "I planted
   * more grass" from "I rooted an enemy in existing grass".
   */
  private _spawnEntangleFx(pos: Pos): void {
    const cx = this._fc(pos.x, pos.y).x;
    const cy = this._fc(pos.x, pos.y).y;
    // Tightening ring — starts wide, "pulses in" tonally via two rings.
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 12, alpha: 1.5,
      color: '#88dd44', timer: 0.40,
    });
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 6, alpha: 1.8,
      color: '#446622', timer: 0.65,
    });
    // 10 vine tendril particles spiraling INWARD to the unit, tangential
    // velocity component to suggest wrapping. Tendrils take a moment
    // longer than sprout particles so they "linger and bind".
    const VINE_COLORS = ['#669933', '#88bb44', '#aaff66', '#446622'];
    for (let p = 0; p < 10; p++) {
      const ang = (p / 10) * Math.PI * 2;
      const startR = CB * 1.2;
      const px = cx + Math.cos(ang) * startR;
      const py = cy + Math.sin(ang) * startR;
      // Tangential + inward radial (wrapping spiral).
      const tangX = -Math.sin(ang);
      const tangY = Math.cos(ang);
      const radX = (cx - px) / startR;
      const radY = (cy - py) / startR;
      const speed = 60 + Math.random() * 35;
      this.fx.push({
        type: 'particle',
        x: px, y: py,
        dx: (tangX * 1.3 + radX * 1.2) * speed,
        dy: (tangY * 1.3 + radY * 1.2) * speed,
        color: VINE_COLORS[Math.floor(Math.random() * VINE_COLORS.length)],
        alpha: 1.7,
        timer: 0.65 + Math.random() * 0.25,
        size: 2 + Math.random() * 2,
      });
    }
    // Slash lines simulating thorny vines — 4 cross strokes through center.
    for (let s = 0; s < 4; s++) {
      const ang = (s / 4) * Math.PI + Math.PI / 8;
      const len = CB * 0.85;
      this.fx.push({
        type: 'slash',
        x1: cx - Math.cos(ang) * len, y1: cy - Math.sin(ang) * len,
        x2: cx + Math.cos(ang) * len, y2: cy + Math.sin(ang) * len,
        color: '#558833', alpha: 1.4, timer: 0.50,
      });
    }
  }

  /**
   * fix#5 — extra zap fx when Lightning hits a unit on a puddle. Spawn
   * a bright cyan shock-ring, 3 zigzag arc lines coming down onto the
   * cell, and a small cloud of cyan-white sparks. Stacks ON TOP of the
   * normal Lightning fx from `_renderSpellFx`.
   */
  private _spawnPuddleZapFx(pos: Pos): void {
    const cx = this._fc(pos.x, pos.y).x;
    const cy = this._fc(pos.x, pos.y).y;
    // Bright shock ring + outer dim ring
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 4, alpha: 2.0,
      color: '#ddf5ff', timer: 0.35,
    });
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 6, alpha: 1.6,
      color: '#88ccff', timer: 0.55,
    });
    // 3 jagged arc slashes — diagonal bolt segments stitched onto the
    // cell. Each `slash` is 2 endpoints; we render 6 slashes total to
    // approximate a zigzag.
    for (let bolt = 0; bolt < 3; bolt++) {
      const baseAng = -Math.PI / 2 + (bolt - 1) * 0.5;
      let lastX = cx + Math.cos(baseAng) * CB * 0.9;
      let lastY = cy + Math.sin(baseAng) * CB * 0.9;
      const segs = 3;
      for (let i = 0; i < segs; i++) {
        const t = (i + 1) / segs;
        const r = (1 - t) * CB * 0.9;
        const ang = baseAng + (Math.random() - 0.5) * 0.9;
        const ex = cx + Math.cos(ang) * r;
        const ey = cy + Math.sin(ang) * r;
        this.fx.push({
          type: 'slash', x1: lastX, y1: lastY, x2: ex, y2: ey,
          color: bolt === 1 ? '#ffffff' : '#aaddff',
          alpha: 1.6, timer: 0.30,
        });
        lastX = ex; lastY = ey;
      }
    }
    // 10 spark particles
    for (let p = 0; p < 10; p++) {
      const ang = Math.random() * Math.PI * 2;
      const speed = 60 + Math.random() * 70;
      this.fx.push({
        type: 'particle',
        x: cx + (Math.random() - 0.5) * CB * 0.3,
        y: cy + (Math.random() - 0.5) * CB * 0.3,
        dx: Math.cos(ang) * speed,
        dy: Math.sin(ang) * speed * 0.7 - 25,
        color: p % 2 === 0 ? '#ffffff' : '#bbeeff',
        alpha: 1.8, timer: 0.45 + Math.random() * 0.25,
        size: 1.5 + Math.random() * 1.5,
      });
    }
  }

  /**
   * c 段 — push water-splash particles when a unit lands on a puddle.
   * Called from move-animation onComplete callbacks so the splash fires
   * as the unit visually arrives at the cell, not 0.5s before. `pos` is
   * in view coords (matches `Game.terrain`).
   */
  private _splashIfPuddle(pos: Pos): void {
    if (!this.terrain) return;
    const row = this.terrain[pos.y];
    if (!row) return;
    const cell = row[pos.x];
    if (cell !== 'puddle') return;
    const cx = this._fc(pos.x, pos.y).x;
    const cy = this._fc(pos.x, pos.y).y;
    // Soft splash ring
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: CB * 0.08,
      color: '#a8d4ff', alpha: 1.2, timer: 0.55,
    });
    // 6 droplets fanning out + up
    const drops = 6;
    for (let i = 0; i < drops; i++) {
      const ang = -Math.PI + (Math.PI * i) / (drops - 1); // -π .. 0 (upper half)
      const speed = 50 + Math.random() * 25;
      this.fx.push({
        type: 'particle',
        x: cx + Math.cos(ang) * 3,
        y: cy + Math.sin(ang) * 2,
        dx: Math.cos(ang) * speed,
        dy: Math.sin(ang) * speed - 30 - Math.random() * 20,
        color: i % 2 === 0 ? '#cce8ff' : '#7aaee0',
        alpha: 1.4,
        timer: 0.5 + Math.random() * 0.15,
        size: 1.5 + Math.random() * 1.2,
      });
    }
  }

  /** Render visual FX for a single spell entry */
  private _renderSpellFx(se: SpellCastRecord): void {
    const cx = this._fc(se.tgt.x, se.tgt.y).x;
    const cy = this._fc(se.tgt.x, se.tgt.y).y;
    const elName = ELEM[se.cmd.spellType];
    const color = ECOL[elName] || '#fff';
    const teamLabel = se.team === this.myTeam ? 'You' : (this.gameMode === 'single' ? 'AI' : 'Opp');

    // Spell name label with caster
    this.fx.push({
      type: 'floatText', x: cx, y: cy - 22,
      text: `${teamLabel}: ${SPELLS[elName]?.name || 'Spell'}`,
      color, alpha: 1.5, timer: 1.4, size: 14,
    });

    // Element-specific visual effects
    if (elName === 'Fire') {
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.5, color: '#ff6600', timer: 0.7 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 8, alpha: 1.0, color: '#ff4444', timer: 0.9 });
      for (let p = 0; p < 10; p++) {
        this.fx.push({
          type: 'particle', x: cx + (Math.random() - 0.5) * CB, y: cy + (Math.random() - 0.5) * CB,
          dx: (Math.random() - 0.5) * 40, dy: -60 - Math.random() * 80,
          color: Math.random() > 0.5 ? '#ff6600' : '#ffaa00', alpha: 1.5, timer: 0.7, size: 3 + Math.random() * 2,
        });
      }
    } else if (elName === 'Water') {
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 2, alpha: 1.2, color: '#44ccff', timer: 0.8 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 10, alpha: 0.8, color: '#4499ff', timer: 1.0 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 18, alpha: 0.5, color: '#2266cc', timer: 1.2 });
    } else if (elName === 'Wind') {
      // Session 7 — Wind became Shield Wall (defense). FX evokes a
      // protective barrier: concentric green rings + vertical "wall"
      // sparks rising from the ground around each ally in radius.
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4, alpha: 1.4, color: '#88ffbb', timer: 0.7 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 12, alpha: 1.0, color: '#33dd88', timer: 1.0 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 22, alpha: 0.6, color: '#22aa66', timer: 1.2 });
      for (let p = 0; p < 10; p++) {
        const ang = (p / 10) * Math.PI * 2;
        this.fx.push({
          type: 'particle',
          x: cx + Math.cos(ang) * CB * 0.8,
          y: cy + Math.sin(ang) * CB * 0.8,
          dx: 0, dy: -50 - Math.random() * 30,
          color: '#aaffcc', alpha: 1.3, timer: 0.7, size: 2.5,
        });
      }
    } else if (elName === 'Light') {
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 2, alpha: 2.0, color: '#ffffaa', timer: 0.5 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 5, alpha: 1.5, color: '#ffcc22', timer: 0.8 });
      for (let p = 0; p < 6; p++) {
        const angle = (p / 6) * Math.PI * 2;
        this.fx.push({
          type: 'particle', x: cx, y: cy,
          dx: Math.cos(angle) * 80, dy: Math.sin(angle) * 80,
          color: '#ffee66', alpha: 1.5, timer: 0.6, size: 3,
        });
      }
    } else if (elName === 'WindFriendly' || elName === 'WindHostile') {
      // Session 9 — whirlwind: swirling rings + tangential particles
      // spiralling inward to the spell center. Hues mirror ECOL:
      // friendly = lime, hostile = magenta (revision: previous teal/rose
      // were too close to Water / Fire on the color wheel).
      const ringCol = elName === 'WindFriendly' ? '#aaee44' : '#ee44bb';
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4,  alpha: 1.4, color: ringCol,  timer: 0.6 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 14, alpha: 1.0, color: ringCol,  timer: 0.9 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 26, alpha: 0.6, color: ringCol,  timer: 1.1 });
      // Tangential swirl — particles start on the rim of radius 2 cells
      // and curl inward with a tangential velocity component.
      const swirlR = CB * 1.8;
      for (let p = 0; p < 14; p++) {
        const ang = (p / 14) * Math.PI * 2;
        const px = cx + Math.cos(ang) * swirlR;
        const py = cy + Math.sin(ang) * swirlR;
        // Tangential (perpendicular to radial) + small inward radial.
        const tangX = -Math.sin(ang);
        const tangY = Math.cos(ang);
        const radX = (cx - px) / swirlR;
        const radY = (cy - py) / swirlR;
        const speed = 95 + Math.random() * 40;
        this.fx.push({
          type: 'particle',
          x: px, y: py,
          dx: (tangX + radX * 0.6) * speed,
          dy: (tangY + radY * 0.6) * speed,
          color: p % 2 === 0 ? ringCol : '#ffffff',
          alpha: 1.4,
          timer: 0.7 + Math.random() * 0.2,
          size: 2.5,
        });
      }
    } else if (elName === 'Grow') {
      // Session 11 (Leader CCC) — Grow casts a wide green burst at target
      // center. Per-cell sprout particles fire from _spawnGrowFx; this
      // is the spell-wide "you cast Grow" hit visual.
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.6, color: '#ddff88', timer: 0.6 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 10, alpha: 1.2, color: '#88dd44', timer: 0.85 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 20, alpha: 0.7, color: '#669933', timer: 1.05 });
      for (let p = 0; p < 6; p++) {
        const angle = (p / 6) * Math.PI * 2;
        this.fx.push({
          type: 'particle', x: cx, y: cy,
          dx: Math.cos(angle) * 70, dy: Math.sin(angle) * 70,
          color: '#aaff66', alpha: 1.4, timer: 0.55, size: 2.5,
        });
      }
    } else if (elName === 'Rain') {
      // Session 11 (Leader CCC) — Rain casts a soft blue burst at target
      // center. Per-cell raindrops fire from _spawnRainFx; this is the
      // spell-wide "you cast Rain" hit visual.
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.5, color: '#aaddff', timer: 0.6 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 10, alpha: 1.1, color: '#66aadd', timer: 0.85 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 20, alpha: 0.7, color: '#3377aa', timer: 1.05 });
      // Falling streaks from above the target center.
      for (let p = 0; p < 8; p++) {
        const dx = (Math.random() - 0.5) * CB * 1.4;
        this.fx.push({
          type: 'particle',
          x: cx + dx, y: cy - CB * 1.4 + Math.random() * CB * 0.5,
          dx: 0, dy: 130 + Math.random() * 60,
          color: Math.random() > 0.5 ? '#aaddff' : '#ffffff',
          alpha: 1.4, timer: 0.5, size: 2,
        });
      }
    } else if (elName === 'Rockfall') {
      // Session 12 (Leader CCC) — Rockfall: brown/earth burst at target
      // center. Per-cell rock streaks fire from _spawnRockfallFx; this
      // is the spell-wide "you cast Rockfall" hit visual. Smaller rings
      // than Grow/Rain because the radius is 0 — visual reads as a
      // focused single-cell impact rather than an AOE.
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.8, color: '#c8b395', timer: 0.45 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 8, alpha: 1.3, color: '#9a7a55', timer: 0.75 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 14, alpha: 0.7, color: '#5a4730', timer: 1.00 });
      // Spell-wide impact crack: a few outward shards diverging from cy.
      for (let p = 0; p < 6; p++) {
        const angle = (p / 6) * Math.PI * 2;
        this.fx.push({
          type: 'particle', x: cx, y: cy,
          dx: Math.cos(angle) * 70, dy: Math.sin(angle) * 70,
          color: '#7a6650', alpha: 1.4, timer: 0.55, size: 2.5,
        });
      }
    } else if (elName === 'Lightning') {
      // Session 7 — Dark renamed to Lightning. FX is a downward strike:
      // bright top-down particle streak + sharp impact ring at target.
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4, alpha: 1.8, color: '#ffffff', timer: 0.25 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 12, alpha: 1.4, color: '#dd99ff', timer: 0.55 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 26, alpha: 0.7, color: '#9944dd', timer: 0.9 });
      // Vertical strike bolt — fast falling particles from above the target
      for (let p = 0; p < 8; p++) {
        const dx = (Math.random() - 0.5) * 20;
        this.fx.push({
          type: 'particle',
          x: cx + dx, y: cy - CB * 1.6 + Math.random() * CB * 0.6,
          dx: 0, dy: 220 + Math.random() * 80,
          color: Math.random() > 0.5 ? '#ffffff' : '#dd88ff',
          alpha: 1.6, timer: 0.4, size: 2.5,
        });
      }
      // Outward sparks at impact
      for (let p = 0; p < 6; p++) {
        const angle = (p / 6) * Math.PI * 2;
        this.fx.push({
          type: 'particle', x: cx, y: cy,
          dx: Math.cos(angle) * 90, dy: Math.sin(angle) * 90,
          color: '#bb55ff', alpha: 1.3, timer: 0.45, size: 2.5,
        });
      }
    } else {
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 5, alpha: 1.2, color, timer: 0.8 });
    }

    // Session 10 — effects-list model: dispatch each impact by which
    // numeric field is set (one impact = one effect on one unit). A
    // single cast can now have multiple impacts per unit (e.g. Shield
    // Wall emits separate shieldPhys + shieldMag impacts; Whirlwind+heal
    // emits a heal impact alongside the move record).
    for (const impact of se.impacts) {
      const u = this.units.find(x => x.id === impact.unitId);
      if (!u || u.dead) continue;
      const ux = this._fc(u.pos.x, u.pos.y).x;
      const uy = this._fc(u.pos.x, u.pos.y).y;

      if (impact.dmg !== undefined && impact.dmg > 0) {
        u.flash = 0.4; u.flashColor = color;
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: '-' + impact.dmg,
          color: '#ff4444', alpha: 1.5, timer: 1.0, size: 14,
        });
      } else if (impact.heal !== undefined && impact.heal > 0) {
        u.flash = 0.3; u.flashColor = '#44ff88';
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: '+' + impact.heal,
          color: '#44ff88', alpha: 1.5, timer: 1.0, size: 14,
        });
      } else if (impact.buff !== undefined && impact.buff !== 0) {
        // Positive = buff (Bless), negative = debuff (Whirlwind hostile).
        const isDebuff = impact.buff < 0;
        u.flash = 0.3; u.flashColor = isDebuff ? '#ee44bb' : '#ffcc22';
        const sign = isDebuff ? '' : '+';   // negative already has '−'
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: `ATK${sign}${impact.buff}`,
          color: isDebuff ? '#ee44bb' : '#ffcc22', alpha: 1.5, timer: 1.0, size: 12,
        });
      } else if (impact.defPhys !== undefined && impact.defPhys > 0) {
        // Shield Wall — physical leg.
        u.flash = 0.3; u.flashColor = '#88ffbb';
        const pct = Math.round(impact.defPhys * 100);
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: `SHIELD ${pct}%`,
          color: '#88ffbb', alpha: 1.5, timer: 1.0, size: 12,
        });
      } else if (impact.defMag !== undefined && impact.defMag > 0) {
        // Shield Wall — magic leg. Position slightly below the phys label
        // so co-cast (Wind = phys + mag in same cast) reads as two lines.
        u.flash = 0.3; u.flashColor = '#88ffbb';
        const pct = Math.round(impact.defMag * 100);
        this.fx.push({
          type: 'floatText', x: ux, y: uy + 4,
          text: `MAGRES ${pct}%`,
          color: '#88ffbb', alpha: 1.4, timer: 1.0, size: 11,
        });
      } else if (impact.rooted !== undefined && impact.rooted > 0) {
        // Session 11 (Leader CCC) — Grow root: vines wrap the target.
        // Color matches Grow's ECOL (#669933) but a touch brighter so it
        // reads on dark unit bodies.
        u.flash = 0.35; u.flashColor = '#669933';
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: '🌿 ROOTED',
          color: '#aaff66', alpha: 1.5, timer: 1.1, size: 12,
        });
        // 纏繞特效：spawn extra vines around the rooted unit (Session 11
        // follow-up). The rooting is the "on existing grass" branch, so
        // this fires whenever Grow lands on a grass-standing enemy.
        this._spawnEntangleFx(u.pos);
      } else if (impact.defDown !== undefined && impact.defDown > 0) {
        // Session 11 follow-up — Grow "open ground" branch: defense down.
        // Reddish-brown floattext (vulnerability hue) so it's not confused
        // with the green rooted label.
        u.flash = 0.35; u.flashColor = '#cc6644';
        const pct = Math.round(impact.defDown * 100);
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: `DEF -${pct}%`,
          color: '#ff9966', alpha: 1.5, timer: 1.1, size: 12,
        });
      }
    }

    // Session 9/10 — whirlwind: per-displaced-unit "swirl" label.
    // The pull effect itself doesn't emit impacts (it emits moves), so
    // we still iterate se.moves to put a visual confirmation on each
    // pulled unit. Color matches the element ECOL palette.
    if (Array.isArray(se.moves) && se.moves.length > 0) {
      const labelCol = elName === 'WindFriendly' ? '#aaee44'
                     : elName === 'WindHostile' ? '#ee44bb'
                     : '#ffffff';
      for (const m of se.moves) {
        const u = this.units.find((x) => x.id === m.unitId);
        if (!u || u.dead) continue;
        u.flash = 0.25; u.flashColor = labelCol;
        const tx = this._fc(m.to.x, m.to.y).x;
        const ty = this._fc(m.to.x, m.to.y).y;
        this.fx.push({
          type: 'floatText', x: tx, y: ty - 10,
          text: '旋',
          color: labelCol, alpha: 1.4, timer: 0.8, size: 14,
        });
      }
    }
  }

  _teamHP(team: number): { healthy: number; total: number } {
    let healthy = 0, total = 0;
    for (const u of this.units) {
      if (u.dead || u.team !== team) continue;
      healthy += u.hp;
      total += u.totalSoldiers;
    }
    return { healthy, total };
  }

  /**
   * Session 10 follow-up — client-side equivalent of the server's
   * end-of-tick modifier decrement loop. Called once per tick boundary
   * crossed (between `tickLabel` events, and one final time at
   * `_finishExec` for the last tick). Keeps Game.units' modifier list
   * (used by the Renderer for badges) aligned with server's lifecycle.
   */
  private _decrementUnitModifiers(): void {
    for (const u of this.units) {
      if (!u.modifiers || u.modifiers.length === 0) continue;
      let expired = false;
      for (let i = u.modifiers.length - 1; i >= 0; i--) {
        u.modifiers[i].ticksRemaining -= 1;
        if (u.modifiers[i].ticksRemaining <= 0) {
          u.modifiers.splice(i, 1);
          expired = true;
        }
      }
      if (expired) _recomputeScalars(u);
    }
  }

  private _finishExec(): void {
    this.phaseBanner = null;
    this.stepActive = false;
    // Session 10 follow-up — final tick's end-of-tick decrement (server
    // does it after Step F of the last tick before returning the events
    // list; we mirror it here so badges expire visually at the right time).
    this._decrementUnitModifiers();
    const t0 = this.units.filter(u => !u.dead && u.team === 0).length;
    const t1 = this.units.filter(u => !u.dead && u.team === 1).length;
    const b0Dead = this.bases.find(b => b.team === 0)?.destroyed ?? false;
    const b1Dead = this.bases.find(b => b.team === 1)?.destroyed ?? false;
    if (t0 === 0 || t1 === 0 || b0Dead || b1Dead || this.round >= this.maxRounds) {
      this._endGame();
    } else {
      this._startRound();
    }
  }

  private _endGame(): void {
    this.state = GS.OVER;
    const hp0 = this._teamHP(0).healthy;
    const hp1 = this._teamHP(1).healthy;
    const b0Dead = this.bases.find(b => b.team === 0)?.destroyed ?? false;
    const b1Dead = this.bases.find(b => b.team === 1)?.destroyed ?? false;
    const t0Dead = this.units.filter(u => !u.dead && u.team === 0).length === 0;
    const t1Dead = this.units.filter(u => !u.dead && u.team === 1).length === 0;

    // Win conditions: enemy units all dead OR enemy base destroyed
    const p0Lost = t0Dead || b0Dead;
    const p1Lost = t1Dead || b1Dead;

    if (p0Lost && p1Lost) this.winner = -1;
    else if (p1Lost) this.winner = 0;
    else if (p0Lost) this.winner = 1;
    else this.winner = hp0 >= hp1 ? 0 : 1;
  }

  restart(): void {
    anim.clear();
    // Close any live network connection before tearing down the battle.
    if (this.adapter && (this.adapter as any).disconnect) {
      try { (this.adapter as any).disconnect(); } catch { /* ignore */ }
    }
    // Phase 3d Step 3: drop any lobby event subscriptions.
    for (const u of this._lobbyUnsubs) { try { u(); } catch { /* ignore */ } }
    this._lobbyUnsubs = [];
    this.units = [];
    this.bases = [];
    this.round = 0;
    this.fx = [];
    this.cardFx = [];
    this.winner = -1;
    this.events = [];
    this.activeLevel = null;
    // Phase 3d Step 3: reset PvP plumbing so next session starts clean.
    this.gameMode = 'single';
    this.myTeam = 0;
    this.roomCode = null;
    this.lobbyMessage = '';
    this.lobbyPending = false;
    this.opponentSessionId = null;
    // Phase 3d Step 4: clear waiting flags too.
    this.waitingForOpponent = false;
    this.awaitingBattleReady = false;
    // Phase 3d Step 5: clear opponent-left flag.
    this.opponentLeft = false;
    // Mirror flag for our own disconnect — cleared on restart so a fresh
    // session doesn't inherit the "(連線中斷)" subtitle.
    this.selfDisconnected = false;
    this.deployDeadline = null;
    // Terrain: clear so the next session re-rolls in startSinglePlayer /
    // startCreateRoom / startJoinRoom.
    this.terrain = null;
    this.terrainSeed = 0;
    // Deployment rules — reset to default manual so the next single /
    // PvP `start*` entry inherits a clean slate.
    this.deployment.setRules(DEFAULT_MANUAL_RULES);
    // Surrender — reset in case we tear down mid-hold or mid-modal.
    this.surrenderHolding = false;
    this.surrenderHoldTime = 0;
    this.surrenderConfirmActive = false;
    resetId();
    this.state = GS.MENU;
  }

  // ----------------------------------------------------------
  // Phase 3d Step 3 — Lobby / room flow
  // ----------------------------------------------------------

  /**
   * Start as the host: build the network adapter, ask server for a
   * fresh room code, then sit in the LOBBY state until the guest joins.
   * Single-player or non-net Mode shouldn't call this (UI guards it).
   */
  async startCreateRoom(): Promise<void> {
    if (this.state !== GS.MENU) return;
    if (this.lobbyPending) return;
    if (!this.netMode) {
      console.warn('[game] startCreateRoom called outside net mode');
      return;
    }
    this.lobbyPending = true;
    this.adapter = new NetworkServerAdapter(this.netUrl);
    this.gameMode = 'pvp-host';
    // PvP currently always uses the default manual rule (5v5 standard).
    // Future PvP-mode picker will let the host choose; server then sends
    // the same rule id to the guest via createRoom RPC.
    this.deployment.setRules(DEFAULT_MANUAL_RULES);
    this.state = GS.LOBBY;
    this.lobbyMessage = 'Connecting...';
    try {
      const res = await this.adapter.createRoom();
      this.roomCode = res.roomCode;
      this.myTeam = res.myTeam;
      // Terrain: server picked a seed at room creation. myTeam = 0 here
      // (host), so view == world; no flip needed.
      this.terrainSeed = res.terrainSeed;
      this.terrain = generateTerrain(this.terrainSeed);
      this.lobbyMessage = 'Room: ' + res.roomCode + ' — waiting for opponent...';
      // Subscribe to opponentJoined push so we can advance to deployment.
      const unsub = this.adapter.onEvent('opponentJoined', (data: any) => {
        this.opponentSessionId = data?.opponentSessionId ?? null;
        this.deployDeadline = data?.deployDeadline ?? null;
        this.lobbyMessage = 'Opponent joined! Loading deployment...';
        // Brief pause so the user reads the message, then go to deploy.
        setTimeout(() => {
          if (this.state === GS.LOBBY) this.startDeployment();
        }, 600);
      });
      this._lobbyUnsubs.push(unsub);
      this._installPvpBattleSubscribers();
    } catch (err) {
      this.lobbyMessage = 'Create room failed: ' + (err as Error).message;
      console.error('[game] createRoom failed:', err);
    } finally {
      this.lobbyPending = false;
    }
  }

  /**
   * Join an existing room as guest. Caller must provide a room code.
   * Single-player or non-net mode shouldn't call this (UI guards it).
   */
  async startJoinRoom(code: string): Promise<void> {
    if (this.state !== GS.MENU) return;
    if (this.lobbyPending) return;
    if (!this.netMode) {
      console.warn('[game] startJoinRoom called outside net mode');
      return;
    }
    if (!code || code.trim().length === 0) {
      console.warn('[game] startJoinRoom called with empty code');
      return;
    }
    this.lobbyPending = true;
    this.adapter = new NetworkServerAdapter(this.netUrl);
    this.gameMode = 'pvp-guest';
    // Guest mirrors host's rules. Currently hardcoded to DEFAULT_MANUAL_RULES;
    // future server-driven mode picker will set this from joinRoom RPC reply.
    this.deployment.setRules(DEFAULT_MANUAL_RULES);
    this.state = GS.LOBBY;
    this.lobbyMessage = 'Joining ' + code + '...';
    try {
      const res = await this.adapter.joinRoom(code.trim().toUpperCase());
      this.roomCode = res.roomCode;
      this.myTeam = res.myTeam;
      // Terrain: server's room-level seed (same as host got). myTeam = 1
      // here (guest), so generate in world coords then flip for view.
      this.terrainSeed = res.terrainSeed;
      this.deployDeadline = res.deployDeadline ?? null;
      this.terrain = flipTerrainForView(generateTerrain(this.terrainSeed));
      this.lobbyMessage = 'Joined ' + res.roomCode + '! Loading deployment...';
      this._installPvpBattleSubscribers();
      // Joiner advances to deployment immediately — pairing is already done.
      setTimeout(() => {
        if (this.state === GS.LOBBY) this.startDeployment();
      }, 400);
    } catch (err) {
      this.lobbyMessage = 'Join failed: ' + (err as Error).message;
      this.state = GS.MENU; // bounce back so user can try a different code
      this.gameMode = 'single';
      console.error('[game] joinRoom failed:', err);
      // Also tear down the half-open adapter to free the socket.
      try { (this.adapter as any).disconnect?.(); } catch { /* ignore */ }
    } finally {
      this.lobbyPending = false;
    }
  }

  /** Start a single-player session (legacy MENU tap behavior).
   *  Single = offline PvP variant — uses the same DEFAULT_MANUAL_RULES
   *  as PvP, so AI mirrors player composition (5 units each). Future
   *  3v3 / 10v10 modes will be addable via a different rule selection. */
  startSinglePlayer(): void {
    if (this.state !== GS.MENU) return;
    this.gameMode = 'single';
    this.myTeam = 0;
    this.roomCode = null;
    this.deployDeadline = null;
    this.activeLevel = null;
    // 單機改關卡制：先進關卡選擇（GS.LEVELS），選關後才佈陣（路徑）。
    this.state = GS.LEVELS;
  }

  /** 選一個關卡：固定雙方單位/位置 + AI 牌組，進入「只畫路徑」的佈陣（preset）。 */
  selectLevel(id: string): void {
    if (this.state !== GS.LEVELS) return;
    const lv = getLevel(id);
    if (!lv) return;
    this.activeLevel = lv;
    this.aiDeckId = lv.aiDeckId;
    // 地形每次選關重新產生（與 startBattle 送 server 的 seed 一致）。
    this.terrainSeed = randomSeed();
    this.terrain = generateTerrain(this.terrainSeed);
    // Deployment: preset 模式（palette 隱藏、不能拖移/增減、只能畫路徑）。
    this.deployment.setRules(PRESET_RULES);
    this.deployment.loadFromUnits(levelPlayerDeployed(lv));
    this.deployment.pathMode = true; // 直接進路徑規劃
    // 關卡佈陣不需要自動存檔 / 讀檔。
    this.deployment.onChange = undefined;
    this.state = GS.DEPLOY;
    this.slotAction = 'none';
  }

  /** 從關卡佈陣退回關卡選擇。 */
  backToLevels(): void {
    if (this.state !== GS.DEPLOY) return;
    this.activeLevel = null;
    this.state = GS.LEVELS;
  }

  /** 關卡 AI 單位 → BattleUnit[]（team 1，各帶朝下 y+ 的前進路徑，遇基地/邊界止）。 */
  private _buildLevelAiUnits(lv: LevelDef): BattleUnit[] {
    const baseTiles = new Set(BASES.flatMap((b) => b.tiles.map((t) => `${t.x},${t.y}`)));
    const out: BattleUnit[] = [];
    let idx = 0;
    for (const u of lv.aiUnits) {
      const cfg = UCFG[u.type];
      if (!cfg) continue;
      const path: Pos[] = [];
      let cy = u.y;
      const steps = Math.min(cfg.maxPath, 5);
      for (let s = 0; s < steps; s++) {
        cy++;
        if (cy >= BATTLE_ROWS) break;
        if (baseTiles.has(`${u.x},${cy}`)) break;
        path.push({ x: u.x, y: cy });
      }
      const unit = mkUnit(u.type, 1, u.x, u.y, cfg.hp, path);
      idx += 1;
      unit.id = 'T1U' + idx;
      out.push(unit);
    }
    return out;
  }

  // ----------------------------------------------------------
  // STORY MODE — chapter picker + chapter bootstrap
  // ----------------------------------------------------------

  // ----------------------------------------------------------
  // Surrender flow — one-tap a button during the card battle (SCHED/EXEC),
  // confirm in a modal, then force-end the battle as a loss.
  // ----------------------------------------------------------

  /** 卡牌模式（M4c）：直接開投降確認 modal（不長按）。 */
  openSurrenderConfirm(): void {
    if (this.state !== GS.SCHED && this.state !== GS.EXEC) return;
    this.surrenderConfirmActive = true;
  }

  /** Dismiss the confirm modal without surrendering (user picked "取消"). */
  dismissSurrenderConfirm(): void {
    this.surrenderConfirmActive = false;
    this.surrenderHolding = false;
    this.surrenderHoldTime = 0;
  }

  /** User confirmed surrender — branch on game mode. */
  confirmSurrender(): void {
    if (!this.surrenderConfirmActive) return;
    this.surrenderConfirmActive = false;
    this.surrenderHolding = false;
    this.surrenderHoldTime = 0;
    // Single / PvP: force a defeat, show GameOver screen.
    if (!this._isLocalMode() && this.adapter) {
      // PvP: drop the socket so the opponent receives `opponentDisconnected`
      // and auto-wins on their side (matches the existing disconnect flow).
      try { (this.adapter as any).disconnect?.(); } catch { /* ignore */ }
    }
    this.state = GS.OVER;
    this.winner = (this.myTeam === 0 ? 1 : 0);
  }

  /**
   * Phase 5a Step 1 — open a browser prompt to edit the WebSocket
   * server URL. Triggered by the "Server" menu button when net mode
   * is on. Empty input clears the saved override (revert to build
   * env / fallback). Invalid input (non ws://) shows an alert and
   * leaves the URL unchanged. The new URL is persisted to
   * localStorage so the next session boots with it.
   *
   * Note: the new URL takes effect on the *next* createRoom /
   * joinRoom call. If the user has already opened an adapter
   * (lobby flow), they need to Cancel back to MENU first.
   */
  promptServerUrl(): void {
    if (typeof window === 'undefined') return;
    const cur = this.netUrl;
    void showPrompt(
      'Server WebSocket URL\n(must start with ws:// or wss://)\nLeave blank to reset to default.',
      cur,
    ).then((input) => {
      if (input === null) return; // user cancelled
      const trimmed = input.trim();
      if (trimmed === '') {
        clearNetUrl();
        this.netUrl = resolveNetUrl();
        console.log(`[game] server URL reset → ${this.netUrl}`);
        return;
      }
      try {
        saveNetUrl(trimmed);
        this.netUrl = trimmed;
        console.log(`[game] server URL set → ${this.netUrl}`);
      } catch (err) {
        console.warn('[game] invalid server URL:', (err as Error).message);
      }
    });
  }

  /**
   * Phase 3d Step 4 — wire up the PvP-only push events. Called once
   * per session (from startCreateRoom / startJoinRoom) right after the
   * adapter is constructed.
   *
   * Events handled:
   *   - battleReady:   server has both initBattle payloads, can start round 1
   *   - execComplete:  server ran exec, here are the events to play back
   */
  private _installPvpBattleSubscribers(): void {
    if (!this.adapter) return;

    const readyUnsub = this.adapter.onEvent('battleReady', (data: any) => {
      console.log('[game] battleReady received — units=' +
        (Array.isArray(data?.units) ? data.units.length : '?') +
        ' bases=' + (Array.isArray(data?.bases) ? data.bases.length : '?'));
      if (!this.awaitingBattleReady) {
        console.warn('[game] battleReady received but not expecting it; ignoring');
        return;
      }
      // Phase 3d Step 4 fix: replace local units/bases with the server's
      // merged authoritative versions so we can render BOTH teams.
      // unitFromJson reattaches the hp/atk getters lost in JSON transit.
      // Then flip world→view if we're the team-1 player (guest).
      if (Array.isArray(data?.units)) {
        this.units = data.units.map((u: any) => {
          const unit = unitFromJson(u);
          if (this.viewFlipped) {
            unit.pos = this._flipPos(unit.pos);
            unit.vx = unit.pos.x;
            unit.vy = unit.pos.y;
            unit.path = unit.path.map((p) => this._flipPos(p));
          }
          return unit;
        });
      }
      if (Array.isArray(data?.bases)) {
        this.bases = data.bases.map((b: any) => {
          const base = baseFromJson(b);
          if (this.viewFlipped) {
            base.tiles = base.tiles.map((t) => this._flipPos(t));
          }
          return base;
        });
      }
      // Terrain: server ships its authoritative grid + seed. Override the
      // client's local copy so any future divergence is reconciled here at
      // the wire boundary (same flip-for-view rule as units / bases).
      if (Array.isArray(data?.terrain)) {
        const worldTerrain = data.terrain as TerrainGrid;
        this.terrain = this.viewFlipped
          ? flipTerrainForView(worldTerrain)
          : worldTerrain.map((row) => row.slice());
      }
      if (typeof data?.terrainSeed === 'number') {
        this.terrainSeed = data.terrainSeed >>> 0;
      }
      this.awaitingBattleReady = false;
      this.waitingForOpponent = false;
      if (this.state !== GS.DEPLOY && this.state !== GS.LOBBY) {
        console.warn('[game] battleReady arrived in unexpected state:', this.state);
        return;
      }
      this._startRound();
    });
    this._lobbyUnsubs.push(readyUnsub);

    const leftUnsub = this.adapter.onEvent('opponentDisconnected', (data: any) => {
      console.log('[game] opponentDisconnected received', data);
      this._handleOpponentLeft();
    });
    this._lobbyUnsubs.push(leftUnsub);

    // Synthetic event emitted by NetworkServerAdapter.onclose when WE
    // lose the socket unexpectedly (network drop / server kill / mobile
    // backgrounding). Without this, the disconnected side would sit
    // frozen behind the "Waiting for opponent" overlay because no server
    // push will ever arrive to clear it. See `_handleConnectionLost`.
    const lostUnsub = this.adapter.onEvent('connectionLost', (data: any) => {
      console.log('[game] connectionLost received', data);
      this._handleConnectionLost();
    });
    this._lobbyUnsubs.push(lostUnsub);

    const execUnsub = this.adapter.onEvent('execComplete', (data: any) => {
      console.log('[game] execComplete received — events=' +
        (Array.isArray(data?.events) ? data.events.length : '?'));
      const rawEvents = Array.isArray(data?.events) ? data.events : [];
      // Phase 3d Step 4 fix: flip event positions for guest's view.
      this.events = this._flipEventsForView(rawEvents);
      this.execPending = false;
      this.waitingForOpponent = false;
      this.stepActive = true;
    });
    this._lobbyUnsubs.push(execUnsub);
  }

  /**
   * Phase 3d Step 4 fix: convert a SpellCommand whose targetPos is in
   * VIEW coords (as stored client-side) into WORLD coords (as expected
   * by the server). Identity when viewFlipped is false.
   */
  private _cmdViewToWorld(cmd: SpellCommand | null): SpellCommand {
    if (!cmd) return { slotIndex: 0, spellType: 0, powerLevel: 0 };
    if (!cmd.targetPos) return cmd;
    return {
      ...cmd,
      targetPos: this._flipPos(cmd.targetPos),
    };
  }

  /**
   * Phase 3d Step 5 — handle the server's opponentDisconnected push.
   *
   * Rule: if the battle has actually started (MATCH3/SCHED/EXEC), award
   * the local player the win and end the game. If we never got past
   * deployment (DEPLOY / pre-battle LOBBY), bounce back to MENU since
   * "winning" without a fight would be weird.
   *
   * Idempotent: subsequent calls (e.g. duplicate pushes) are no-ops.
   */
  private _handleOpponentLeft(): void {
    if (this.opponentLeft) return;
    this.opponentLeft = true;
    this.adapterError = 'Opponent disconnected';

    const inBattle =
      this.state === GS.SCHED ||
      this.state === GS.EXEC;

    if (inBattle) {
      // Concede on the opponent's behalf; we win.
      this.winner = this.myTeam;
      this.waitingForOpponent = false;
      this.execPending = false;
      this.stepActive = false;
      // Drop any in-flight playback so we land cleanly on the OVER screen.
      this.events = [];
      this.fx = [];
      try { anim.clear(); } catch { /* ignore */ }
      this.state = GS.OVER;
      return;
    }

    // Pre-battle (MENU / LOBBY / DEPLOY): force LOBBY state so the user
    // SEES the disconnect message (DEPLOY screen doesn't render
    // lobbyMessage). Then bounce to MENU after a beat.
    this.lobbyMessage = 'Opponent disconnected. Returning to menu...';
    this.awaitingBattleReady = false;
    this.waitingForOpponent = false;
    this.state = GS.LOBBY;
    setTimeout(() => {
      // Only bounce if we're still in LOBBY — user might have already
      // navigated themselves (e.g. clicked Cancel).
      if (this.state === GS.LOBBY) this.restart();
    }, 1800);
  }

  /**
   * Handle OUR socket closing unexpectedly while a PvP session was live.
   * Mirror of `_handleOpponentLeft` for the disconnected side — the
   * server (and the other player) will see the equivalent
   * `opponentDisconnected` push; we synthesise this event locally so
   * the disconnected client doesn't sit frozen on "Waiting for opponent".
   *
   * Rule: if the battle has actually started (MATCH3/SCHED/EXEC), the
   * local player concedes (we know the opponent will be awarded the win
   * server-side). If we never got past deployment (DEPLOY / pre-battle
   * LOBBY), bounce back to MENU since "losing" without a fight would be
   * weird.
   *
   * Idempotent: subsequent calls (e.g. double-fire from queued RPC
   * rejections triggering another path) are no-ops.
   *
   * Skipped when:
   *   - Local mode (single / story) — no real socket, shouldn't trigger
   *   - Already on OVER or MENU — surrender / opponentLeft already
   *     resolved the session; don't clobber their result
   */
  private _handleConnectionLost(): void {
    if (this.selfDisconnected) return;
    if (this._isLocalMode()) return;
    if (this.state === GS.OVER || this.state === GS.MENU) return;

    this.selfDisconnected = true;
    this.adapterError = 'Connection lost';

    const inBattle =
      this.state === GS.SCHED ||
      this.state === GS.EXEC;

    if (inBattle) {
      // We lost the connection → server will award the opponent the win
      // (or has already). Mirror that locally so the OVER screen reads
      // DEFEAT with a "(連線中斷)" subtitle (see Renderer._gameOver).
      this.winner = (this.myTeam === 0 ? 1 : 0);
      this.waitingForOpponent = false;
      this.execPending = false;
      this.stepActive = false;
      // Drop any in-flight playback so we land cleanly on the OVER screen.
      this.events = [];
      this.fx = [];
      try { anim.clear(); } catch { /* ignore */ }
      this.state = GS.OVER;
      return;
    }

    // Pre-battle (LOBBY / DEPLOY): force LOBBY state so the user SEES
    // the disconnect message (DEPLOY screen doesn't render
    // lobbyMessage). Then bounce to MENU after a beat — matches the
    // pre-battle path of `_handleOpponentLeft`.
    this.lobbyMessage = '連線中斷，返回主選單...';
    this.awaitingBattleReady = false;
    this.waitingForOpponent = false;
    this.state = GS.LOBBY;
    setTimeout(() => {
      if (this.state === GS.LOBBY) this.restart();
    }, 1800);
  }

}

